import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import SmartStaysLogo from '../components/SmartStaysLogo';
import PlatformBadge from '../components/PlatformBadge';
import { PropertyFilter, PropertyPicker, usePropertySelection } from '../components/PropertyFilter';
import ErrorBoundary from '../components/ErrorBoundary';
import '../components/CalendarHub.css';
import { validateRequiredFields, clearFieldInvalid } from '../utils/formValidation';
import { getAuthToken, getAuthUser } from '../utils/authSession';
import '../components/PropertyCalendar.css';
import '../components/PropertyTimeline.css';
import '../components/AutomationOverview.css';
import '../components/CleaningOverview.css';
import '../components/NotificationBell.css';
import '../components/ProfileMenu.css';
import './Dashboard.css';
import './DemoDashboard.css';

import i18n from '../i18n';
import { useTranslation } from 'react-i18next';

// Demo-teksten per taal. Sleutel = de Nederlandse tekst, waarde = [en, fr].
// Waar de echte app al een vertaling heeft, is dezelfde formulering gebruikt.
const DEMO_TEXT = {
  'Studio Centrum': ['City Centre Studio', 'Studio Centre-ville'],
  'Appartement Zeezicht': ['Seaview Apartment', 'Appartement Vue mer'],
  'Loft Historisch Centrum': ['Historic Centre Loft', 'Loft Centre historique'],
  'Penthouse Dakterras': ['Rooftop Penthouse', 'Penthouse Toit-terrasse'],
  'Cottage Buitenrand': ['Countryside Cottage', 'Cottage Campagne'],
  'Duplex Stationsbuurt': ['Station Area Duplex', 'Duplex Quartier gare'],
  'Omzet': ['Revenue', "Chiffre d'affaires"],
  'Laatste {{n}} dagen': ['Last {{n}} days', '{{n}} derniers jours'],
  'Bezettingsgraad': ['Occupancy rate', "Taux d'occupation"],
  'Kanaalmix': ['Channel mix', 'Canaux'],
  'Nog geen boekingen': ['No bookings yet', 'Pas encore de réservations'],
  'Binnenkort': ['Upcoming', 'À venir'],
  'Geen komende in-/uitchecks': ['No upcoming check-ins/outs', 'Aucun check-in/out à venir'],
  'Beoordelingen': ['Reviews', 'Avis'],
  'Gemiddelde score over {{n}} reviews (Airbnb, Booking.com, ...)': ['Average score over {{n}} reviews (Airbnb, Booking.com, ...)', 'Note moyenne sur {{n}} avis (Airbnb, Booking.com, ...)'],
  '{{n}} reviews': ['{{n}} reviews', '{{n}} avis'],
  'Schoonmaak': ['Cleaning', 'Nettoyage'],
  'Aankomend': ['Upcoming', 'À venir'],
  'Voltooid': ['Completed', 'Terminé'],
  '(laatste 30 dagen)': ['(last 30 days)', '(30 derniers jours)'],
  'Voltooid (laatste 30 dagen)': ['Completed (last 30 days)', 'Terminé (30 derniers jours)'],
  'Geen aankomende schoonmaakmomenten.': ['No upcoming cleanings.', 'Aucun nettoyage à venir.'],
  'Nog geen voltooide schoonmaakmomenten.': ['No completed cleanings yet.', 'Aucun nettoyage terminé pour le moment.'],
  'Vorige': ['Previous', 'Précédent'],
  'Vandaag': ['Today', "Aujourd'hui"],
  'Volgende': ['Next', 'Suivant'],
  'Multi-kalender': ['Multi-calendar', 'Multi-calendrier'],
  'Maandkalender': ['Month calendar', 'Calendrier mensuel'],
  'Meldingen': ['Notifications', 'Notifications'],
  'Alles gelezen': ['Mark all as read', 'Tout marquer comme lu'],
  'Geen meldingen': ['No notifications', 'Aucune notification'],
  'Je gast': ['Your guest', 'Votre hôte'],
  '{{name}} is ingecheckt bij {{prop}}.': ['{{name}} has checked in at {{prop}}.', '{{name}} a fait son check-in à {{prop}}.'],
  'Batterijniveau slim slot {{prop}}: 34%. Laad binnenkort op.': ['Smart lock battery level {{prop}}: 34%. Recharge soon.', 'Niveau de batterie de la serrure {{prop}} : 34 %. À recharger bientôt.'],
  'Schoonmaker is toegekomen bij {{prop}}.': ['Cleaner has arrived at {{prop}}.', 'Le nettoyeur est arrivé à {{prop}}.'],
  'Schoonmaak voltooid bij {{prop}}.': ['Cleaning completed at {{prop}}.', 'Nettoyage terminé à {{prop}}.'],
  '2 uur geleden': ['2 hours ago', 'il y a 2 heures'],
  'gisteren': ['yesterday', 'hier'],
  '2 dagen geleden': ['2 days ago', 'il y a 2 jours'],
  'Kies een woning': ['Choose a property', 'Choisir un logement'],
  'Deurslot': ['Door lock', 'Serrure'],
  'Vergrendeld': ['Locked', 'Verrouillée'],
  'Ontgrendeld': ['Unlocked', 'Déverrouillée'],
  'Ontgrendel': ['Unlock', 'Déverrouiller'],
  'Vergrendel': ['Lock', 'Verrouiller'],
  'Batterijstatus': ['Battery status', 'État de la batterie'],
  'Synchroniseer': ['Sync', 'Synchroniser'],
  'Batterij keypad OK': ['Keypad battery OK', 'Batterie du clavier OK'],
  'Acties': ['Actions', 'Actions'],
  'Bekijken': ['View', 'Voir'],
  'Verbergen': ['Hide', 'Masquer'],
  'Geen belangrijke acties gevonden.': ['No important actions found.', 'Aucune action importante trouvée.'],
  'Volledige geschiedenis bekijken': ['View full history', "Voir tout l'historique"],
  'Huidige gastcode': ['Current guest code', "Code de l'hôte actuel"],
  'Geen gast momenteel ingecheckt': ['No guest currently checked in', 'Aucun hôte actuellement présent'],
  '{{name}} - vervalt bij check-out': ['{{name}} - expires at check-out', '{{name}} - expire au check-out'],
  'Mijn code': ['My code', 'Mon code'],
  'Kinderen': ['Children', 'Enfants'],
  'Persoonlijke codes': ['Personal codes', 'Codes personnels'],
  'Code toevoegen': ['Add code', 'Ajouter un code'],
  'Accommodaties': ['Properties', 'Logements'],
};
const demoLang = () => {
  const l = String((i18n && i18n.language) || 'nl').slice(0, 2);
  return l === 'en' || l === 'fr' ? l : 'nl';
};
const dt = (nl, vars) => {
  const l = demoLang();
  const entry = DEMO_TEXT[nl];
  let out = l === 'nl' || !entry ? nl : entry[l === 'en' ? 0 : 1];
  if (vars) Object.keys(vars).forEach((k) => { out = out.split(`{{${k}}}`).join(String(vars[k])); });
  return out;
};
const demoLocale = (variant) => {
  const l = demoLang();
  if (l === 'en') return 'en-GB';
  if (l === 'fr') return 'fr-BE';
  return variant === 'NL' ? 'nl-NL' : 'nl-BE';
};

// Losstaande, volledig lokale (nep) kopie van het verhuurder-dashboard om
// tijdens een verkoopgesprek te tonen. Hergebruikt bewust de ECHTE
// Dashboard.css/PropertyCalendar.css/AutomationOverview.css-klassen en een
// JSX-structuur die de echte pagina's op de voet volgt, zodat dit er ook
// écht hetzelfde uitziet - enkel de databron is nep (lokale React-state
// i.p.v. axios-calls), gevoed door de querystring die de "Demo"-tab in
// Admin.js meegeeft (?access=1&automation=1&tier=FULL&count=2). Geen enkele
// interactie (slot vergrendelen, code toevoegen, schoonmaak inplannen, ...)
// raakt ooit de echte backend.

const PROPERTY_NAMES = [
  'Studio Centrum',
  'Appartement Zeezicht',
  'Loft Historisch Centrum',
  'Penthouse Dakterras',
  'Cottage Buitenrand',
  'Duplex Stationsbuurt'
];

// Realistische, per type/grootte woning verschillende nachtprijs (i.p.v.
// vroeger 1 vaste €95 voor elke woning, wat de omzet van elke accommodatie
// even hoog liet ogen) - zelfde volgorde als PROPERTY_NAMES hierboven, zie
// buildAutomationData verderop.
const PROPERTY_NIGHTLY_RATES = [75, 110, 130, 195, 105, 140];

// Bewust VERZONNEN straatnamen (geen bestaande, herkenbare pleinen/straten
// zoals voorheen "Korenmarkt"/"Grote Markt" - dat wekte de indruk dat het om
// een echt, opzoekbaar pand op een bekende locatie ging). Enkel de steden
// zijn echt (past bij een Belgisch bedrijf), de straatnaam zelf niet.
const DEMO_ADDRESSES = [
  'Lindenhof 14, 9000 Gent',
  'Meeuwenlaan 27, 8400 Oostende',
  'Populierendreef 5, 9000 Gent',
  'Vossenberg 31, 2000 Antwerpen',
  'Beukenhage 9, 8000 Brugge',
  'Sterrenveld 18, 9000 Gent'
];

// Nep-fotootjes per woning (echte verhuurders zien hier hun eigen geüploade
// foto - zie photoUrl/property-photo-thumb in het echte Dashboard.js).
// Eerder probeerden we hier telkens een externe stockfoto-dienst
// (LoremFlickr, later ook picsum.photos als fallback) te tonen, maar die
// bleven - zelfs met een 2-staps-fallback - af en toe gewoon niet laden
// (netwerk/adblocker/rate limit), wat steeds opnieuw een kapotte/lege foto
// gaf tijdens een verkoopgesprek. Een simpele effen gradiënt zonder foto oogde
// dan weer te kaal. Dit zijn daarom eigen, lokaal meegeleverde illustraties
// (frontend/public/demo-photos/*.svg) i.p.v. een externe URL - geen enkele
// netwerk-afhankelijkheid meer, dus kan nooit meer stuk gaan, en toont toch
// een echt "fotootje" per woning i.p.v. een kaal icoontje.
const DEMO_PHOTOS = [
  '/demo-photos/studio-centrum.svg',
  '/demo-photos/appartement-zeezicht.svg',
  '/demo-photos/loft-historisch-centrum.svg',
  '/demo-photos/penthouse-dakterras.svg',
  '/demo-photos/cottage-buitenrand.svg',
  '/demo-photos/duplex-stationsbuurt.svg'
];

// 24 namen = 8 per woning (zie GUEST_NAMES_PER_PROPERTY hieronder) zonder
// overlap voor tot en met 3 woningen tegelijk (de meest voorkomende demo-
// situatie) - voorheen kregen 2 woningen met slechts 6 namen en een simpele
// +1-offset per woning grotendeels DEZELFDE namen te zien, wat raar oogde.
// Komen er per woning meer boekingen dan deze 8 namen (zie buildInitialEvents
// hieronder), dan wordt binnen diezelfde woning gewoon opnieuw bij de eerste
// naam begonnen (een terugkerende gast) i.p.v. over te lopen in het blok van
// de volgende woning.
// BELANGRIJK: bij een 4de (of verdere) woning liep het blok
// (propIndex * GUEST_NAMES_PER_PROPERTY) buiten deze 24-namen-array, wat
// `undefined` als gastnaam gaf en de hele demo deed crashen zodra
// emailFor(undefined) er .toLowerCase() op probeerde toe te passen (zie
// GUEST_NAMES.length hieronder bij de "guest"-functie in buildInitialEvents,
// die nu wrapt i.p.v. buiten de array te lopen). Vanaf de 4de woning worden
// namen dus hergebruikt (herkenbaar overlap tussen woningen) - niet mooi,
// maar crasht niet meer, en dit blijft ver onder de max. van 6 woningen die
// de Demo-tab in Admin.js toelaat.
const GUEST_NAMES = [
  'Tom Peeters', 'M. Janssens', 'Sophie Lambrechts', 'Dhr. De Clercq',
  'Emma Willems', 'Wouter Maes', 'Lars Van Damme', 'Julie Coppens',
  'Karim Benali', 'Mevr. Bogaert', 'Noah Verhelst', 'Eva Van Acker',
  'Anna Peeters', 'Bram Wouters', 'Nele De Ridder', 'Ines Vermeulen',
  'Youssef El Amrani', 'Charlotte Goossens', 'Dhr. Wauters', 'Milan Dekeyser',
  'Fien Claes', 'Robbe Pieters', 'Sara Van den Berghe', 'Olivier Mertens'
];
const GUEST_NAMES_PER_PROPERTY = 8;

// Roteren over meerdere schoonmakers i.p.v. altijd dezelfde ene naam -
// toont dat SmartStays met een heel team werkt, niet met 1 vaste persoon.
const CLEANER_NAMES = ['Els', 'Laura', 'Bert'];

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

// Zelfde eigen-maandweergave-helpers als het echte PropertyCalendar.js (zie
// het commentaar daar bij ".month-view" in PropertyCalendar.css) - hier los
// gedefinieerd voor de demo-versie van de per-woning kalender.
function addMonths(date, months) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}
function startOfMonth(date) {
  const d = startOfDay(date);
  d.setDate(1);
  return d;
}
// 0 = maandag, ..., 6 = zondag (i.p.v. JS' eigen 0 = zondag).
function mondayIndex(date) {
  return (date.getDay() + 6) % 7;
}

// Zelfde parameters als het echte PropertyTimeline.js (Multi-kalender), zie
// hieronder bij DemoTimeline - hier los gedefinieerd zodat deze module geen
// afhankelijkheid heeft van dat component (dat doet immers zijn eigen
// axios-calls en kan hier dus niet zomaar hergebruikt worden).
const TIMELINE_PAST_DAYS = 30;
const TIMELINE_WINDOW_DAYS = 90;
const TIMELINE_DESKTOP_PX_PER_DAY = 64;
const TIMELINE_MOBILE_PX_PER_DAY = 34;
const TIMELINE_MIN_BAR_WIDTH_PX = 4;
const defaultTimelineViewStart = () => addDays(startOfDay(new Date()), -TIMELINE_PAST_DAYS);

function atHour(date, hours, minutes = 0) {
  const d = new Date(date);
  d.setHours(hours, minutes, 0, 0);
  return d;
}

const fmtTime = (d) => new Date(d).toLocaleTimeString(demoLocale(), { hour: '2-digit', minute: '2-digit' });
const fmtDate = (d) => new Date(d).toLocaleDateString(demoLocale(), { day: '2-digit', month: '2-digit' });
const fmtDateOnly = (d) => new Date(d).toLocaleDateString(demoLocale());
const fmtDateTime = (d) => new Date(d).toLocaleString(demoLocale(), { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const emailFor = (name) => `${name.toLowerCase().replace(/[^a-z]+/g, '.')}@voorbeeld.be`;

function buildDemoProperties(count, access, automation, tier) {
  const props = [];
  for (let i = 0; i < count; i++) {
    props.push({
      id: `demo-${i + 1}`,
      name: dt(PROPERTY_NAMES[i % PROPERTY_NAMES.length]),
      address: DEMO_ADDRESSES[i % DEMO_ADDRESSES.length],
      photoUrl: DEMO_PHOTOS[i % DEMO_PHOTOS.length],
      accessManagementEnabled: access,
      automationEnabled: automation,
      cleaningTier: tier,
      cleaningHours: 2
    });
  }
  return props;
}

// Boekingen + schoonmaaktaken voor 1 woning, verspreid rond vandaag zodat de
// kalender er meteen "levendig" uitziet zodra de demo geopend wordt. Elke
// woning krijgt een eigen aantal boekingen (7 tot 10) met wisselende
// verblijfsduur en wisselende leegstand ertussen - i.p.v. steeds exact
// dezelfde vaste verschuiving van 3 nachten om de zoveel dagen, wat als "een
// patroontje" oogde in plaats van een levensecht boekingsritme. Er blijft
// wel altijd gegarandeerd: 1 lopende boeking (gast nu aanwezig, dus nog geen
// schoonmaak nodig), 1 al afgehandelde schoonmaak (groen/toegewezen), 1 nog
// niet bevestigde schoonmaak (roze/in afwachting) en minstens 1 boeking
// zonder ingeplande schoonmaak (zichtbaar via "+ Schoonmaak toevoegen") -
// de rest van de boekingen krijgt willekeurig een van die drie statussen.
// De generator gebruikt een eigen kleine seeded random (geen Math.random)
// zodat elke woning een ander patroon krijgt, maar dat patroon binnen 1
// paginalading stabiel blijft i.p.v. bij elke re-render te veranderen.
function buildInitialEvents(propIndex, hasAccessManagement, cleaningTier) {
  const today = new Date();

  let seed = propIndex * 9301 + 49297;
  const rand = () => {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280;
  };
  const randInt = (min, max) => min + Math.floor(rand() * (max - min + 1));

  const guest = (i) => GUEST_NAMES[(propIndex * GUEST_NAMES_PER_PROPERTY + (i % GUEST_NAMES_PER_PROPERTY)) % GUEST_NAMES.length];
  // "Direct" (geen kanaal, dus geen logo) verving door "Vrbo" - anders had
  // ongeveer 1 op 3 demo-boekingen nooit een platform-logo'tje, wat net het
  // punt van deze feature (zie PlatformBadge.js) tenietdeed.
  const PLATFORMS = ['Airbnb', 'Booking.com', 'Vrbo'];

  // Marktaandeel per woning: het exacte percentage verschilt van woning tot
  // woning (zodat de kanaalmix niet bij elke accommodatie identiek oogt),
  // maar over alle woningen heen ligt Airbnb altijd het hoogst, dan
  // Booking.com, dan Vrbo - zoals ook in de praktijk meestal het geval is.
  const airbnbWeight = 0.45 + rand() * 0.15; // 45-60%
  const remainingWeight = 1 - airbnbWeight;
  const bookingShareOfRemaining = 0.55 + rand() * 0.15; // 55-70% van de rest
  const bookingWeight = remainingWeight * bookingShareOfRemaining;
  const vrboWeight = remainingWeight - bookingWeight;
  const platformWeights = [airbnbWeight, bookingWeight, vrboWeight]; // zelfde volgorde als PLATFORMS
  const pickPlatform = () => {
    const r = rand();
    let cumulative = 0;
    for (let p = 0; p < PLATFORMS.length; p++) {
      cumulative += platformWeights[p];
      if (r <= cumulative) return PLATFORMS[p];
    }
    return PLATFORMS[PLATFORMS.length - 1];
  };

  // De lopende boeking: gast is nu effectief aanwezig (ingecheckt gisteren
  // of eergisteren, checkt uit binnen 1-3 dagen).
  const activeCheckIn = atHour(addDays(today, -randInt(1, 2)), 15, 0);
  const activeCheckOut = atHour(addDays(today, randInt(1, 3)), 11, 0);
  const specs = [{ checkIn: activeCheckIn, checkOut: activeCheckOut }];

  // Verleden: teruglopend vanaf de lopende boeking tot minstens 33 dagen
  // terug (iets meer dan de volle maand die het "Schoonmaak"-overzicht z'n
  // "Voltooid"-lijst moet bestrijken), met wisselende verblijfsduur (3-8
  // nachten) en wisselende (meestal korte) leegstand ertussen (40% kans op
  // meteen dezelfde dag een nieuwe gast, anders 1-3 dagen) - deze verhouding
  // geeft een bezettingsgraad die realistisch rond en boven de 70% uitkomt
  // i.p.v. de vorige, wat lagere verhouding. Voorheen was dit ook een vast,
  // willekeurig AANTAL boekingen (soms nog geen 2 weken terug) i.p.v. een
  // vast aantal DAGEN terug - vandaar de while-lus i.p.v. een for-lus met
  // vaste teller.
  const pastHorizon = addDays(today, -33);
  let cursor = activeCheckIn;
  let pastCount = 0;
  while (cursor > pastHorizon && pastCount < 20) {
    const gap = rand() < 0.4 ? 0 : randInt(1, 3);
    const nights = randInt(3, 8);
    const checkOut = addDays(cursor, -gap);
    const checkIn = addDays(checkOut, -nights);
    specs.unshift({ checkIn: atHour(checkIn, 15, 0), checkOut: atHour(checkOut, 11, 0) });
    cursor = checkIn;
    pastCount++;
  }
  const activeIdx = pastCount;

  // Toekomst: vast aantal komende boekingen, zelfde opbouwlogica.
  const futureCount = randInt(4, 6);
  cursor = activeCheckOut;
  for (let i = 0; i < futureCount; i++) {
    const gap = rand() < 0.4 ? 0 : randInt(1, 3);
    const nights = randInt(3, 8);
    const checkIn = addDays(cursor, gap);
    const checkOut = addDays(checkIn, nights);
    specs.push({ checkIn: atHour(checkIn, 15, 0), checkOut: atHour(checkOut, 11, 0) });
    cursor = checkOut;
  }

  const events = [];
  let cleanerRotation = 0;
  specs.forEach((spec, i) => {
    const bookingId = `b${i}-${propIndex}`;
    const guestName = guest(i);
    const platform = pickPlatform();
    const isActive = i === activeIdx;
    const isPast = i < activeIdx;
    const isFirstFuture = i === activeIdx + 1;
    const isLast = i === specs.length - 1;

    let hasCleaning = false;
    let status = null;
    let cleanerName = null;
    // Bij "Schoonmaak: Niet" (cleaningTier === 'NONE', ingesteld in Admin.js
    // z'n Demo-config) mag hier NOOIT een schoonmaaktaak ontstaan - dit
    // simuleert een klant die geen schoonmaakdienst afneemt, dus geen
    // Schoonmaak-tab, geen balkjes op de kalenders. Voorheen werd "tier"
    // enkel als label op de woning gezet maar hier volledig genegeerd,
    // waardoor er alsnog schoonmaaktaken verschenen ongeacht de gekozen
    // instelling.
    if (cleaningTier === 'NONE') {
      hasCleaning = false;
    } else if (isActive) {
      hasCleaning = false;
    } else if (isLast) {
      // Verste toekomstige boeking: altijd nog te plannen, zodat er altijd
      // minstens 1 item overblijft om in te plannen.
      hasCleaning = false;
    } else if (isPast) {
      // Elke boeking in het verleden (checkout al voorbij) is normaal gezien
      // allang toegewezen ÉN voltooid - kan hier dus nooit "nog te
      // bevestigen" of zonder schoonmaker staan, anders oogt dat raar in de
      // "Voltooid"-lijst van het Schoonmaak-overzicht (en in de kalenders,
      // die een schoonmaak in het verleden altijd groen tonen).
      hasCleaning = true; status = 'ASSIGNED'; cleanerName = CLEANER_NAMES[cleanerRotation++ % CLEANER_NAMES.length];
    } else if (isFirstFuture) {
      // Eerstvolgende boeking na de lopende: al ingepland én een schoonmaker
      // toegewezen (een naam in de lijst "Aankomend").
      hasCleaning = true; status = 'ASSIGNED'; cleanerName = CLEANER_NAMES[cleanerRotation++ % CLEANER_NAMES.length];
    } else if (propIndex === 0 && i === activeIdx + 3) {
      // Precies 1 latere schoonmaak (over alle woningen heen) heeft nog geen
      // schoonmaker (rood, zonder naam); alle andere komende schoonmaken hebben
      // een naam. Altijd een andere boeking dan de laatste (die blijft "nog te
      // plannen"), want er zijn minstens 4 toekomstige boekingen.
      hasCleaning = true; status = 'PENDING_CONFIRMATION';
    } else {
      hasCleaning = rand() >= 0.2;
      if (hasCleaning) {
        status = 'ASSIGNED';
        cleanerName = CLEANER_NAMES[cleanerRotation++ % CLEANER_NAMES.length];
      }
    }

    events.push({
      id: bookingId,
      title: `${guestName} - ${platform} (${fmtTime(spec.checkIn)} → ${fmtTime(spec.checkOut)})`,
      start: spec.checkIn, end: spec.checkOut, allDay: false,
      backgroundColor: '#0d47a1', borderColor: '#0d47a1', textColor: 'white',
      extendedProps: { type: 'booking', guestName, guestEmail: emailFor(guestName), hasCleaning, platform }
    });

    if (hasCleaning) {
      // Ingeplande schoonmaak-uren zijn in de praktijk altijd mooie ronde
      // uren (bv. 11:00-15:00), nooit iets als "12:00-13:54" - dat laatste
      // zou het geplande tijdstip verwarren met het exacte moment waarop de
      // schoonmaker toevallig is toegekomen (dat is arrivedAt hieronder, en
      // hoort enkel in het Schoonmaak-tabblad thuis, niet hier). Daarom hier
      // zowel start als duur op een volledig uur afgerond.
      const cStart = atHour(spec.checkOut, 11 + randInt(0, 2), 0);
      const cEnd = new Date(cStart.getTime() + randInt(2, 4) * 60 * 60000);
      const isAssigned = status === 'ASSIGNED';
      // Nu en dan een toekomstige schoonmaak waarvoor nog niet alle schoonmakers
      // zijn aangemeld (1 van 2) - toont de oranje 'gedeeltelijk bezet'-kleur.
      const isPartial = isAssigned && !isPast && rand() < 0.3;
      // Effectief aankomstmoment van de schoonmaker, enkel relevant voor al
      // voltooide taken (zie "Voltooid" in het Schoonmaak-overzicht): met
      // toegangsbeheer/smartlock actief is dit het moment waarop die effectief
      // ontgrendeld werd - in het echt nooit exact het geplande startuur, dus
      // hier ook een kleine, realistische afwijking (iets vroeger tot een
      // kwartier later) i.p.v. altijd stipt 12:00/12:30. Zonder toegangsbeheer
      // bestaat er geen smartlock-gebeurtenis om op te tonen, dus dan blijft
      // dit gewoon het geplande startuur.
      const arrivedAt = (isPast && hasAccessManagement)
        ? new Date(cStart.getTime() + randInt(-6, 16) * 60000)
        : cStart;
      events.push({
        id: `c${i}-${propIndex}`,
        title: `Schoonmaak (${fmtTime(cStart)} → ${fmtTime(cEnd)})`,
        start: cStart, end: cEnd, allDay: false,
        backgroundColor: isAssigned ? '#4caf50' : '#ef9a9a',
        borderColor: isAssigned ? '#4caf50' : '#ef9a9a',
        textColor: 'white',
        extendedProps: { type: 'cleaning', status, cleanerName, bookingId, arrivedAt, partial: isPartial }
      });
    }
  });

  return events;
}

function buildInitialCodes() {
  return [
    { id: 1, name: dt('Mijn code'), code: '481297' },
    { id: 2, name: dt('Kinderen'), code: '119734' }
  ];
}

// Meest recente actie eerst (zoals een echte openingsgeschiedenis/actielijst
// altijd toont) - voorheen stond dit gewoon in de volgorde waarin de acties
// hieronder getypt waren, wat niet overeenkwam met de tijdstippen zelf.
// Gebruikt de echte gastnaam/schoonmakernaam van déze woning (uit haar eigen
// boekingen/schoonmaaktaken) i.p.v. generieke labels, zodat de "belangrijke
// acties"-filter hieronder (zie importantSmartlockLog in de JSX) er ook in de
// demo realistisch uitziet - en er is bewust 1 naamloos item (rechtstreeks
// via de Nuki-app, geen gekoppelde code) dat enkel in de volledige
// geschiedenis opduikt, net om dat onderscheid te kunnen tonen.
function buildFakeLog(propertyEvents, personalCodes) {
  const now = new Date();
  const h = (hoursAgo) => new Date(now.getTime() - hoursAgo * 60 * 60 * 1000);
  const events = propertyEvents || [];
  const activeBooking = events.find((e) => e.extendedProps.type === 'booking' && e.extendedProps.guestName);
  const guestName = activeBooking?.extendedProps.guestName || 'Gastcode';
  const cleaningWithCleaner = events.find((e) => e.extendedProps.type === 'cleaning' && e.extendedProps.cleanerName);
  const cleanerName = cleaningWithCleaner?.extendedProps.cleanerName;
  const personalCodeName = personalCodes?.[0]?.name || dt('Mijn code');

  const entries = [
    { action: 1, name: guestName, date: h(2) },
    { action: 2, name: personalCodeName, date: h(20) },
    { action: 1, name: personalCodeName, date: h(21) },
    { action: 2, name: null, date: h(30) },
  ];
  if (cleanerName) {
    entries.push({ action: 1, name: cleanerName, date: h(47) });
    entries.push({ action: 2, name: cleanerName, date: h(45) });
  }
  return entries.sort((a, b) => b.date - a.date);
}

// Zelfde soort cijfers als het echte /api/automation-overview zou teruggeven,
// hier berekend uit de nep-boekingen van de meegegeven woning(en) i.p.v. een
// echte databasequery.
// Haalt het stabiele volgnummer uit een demo-woning-id ("demo-3" -> 2), zodat
// de nep-reviewscore van een woning altijd dezelfde blijft ongeacht of ze
// bekeken wordt via het overkoepelende Automation-paneel (alle woningen) of
// via haar eigen per-woning Automation-tab (waar propsSubset maar 1 element
// bevat en de array-index dus altijd 0 zou zijn).
function demoPropertyIndex(property) {
  const match = /^demo-(\d+)$/.exec(property.id);
  return match ? parseInt(match[1], 10) - 1 : 0;
}

const REVIEW_SCORES = [4.9, 4.7, 4.8, 4.6, 5.0, 4.7];

function buildAutomationData(propsSubset, eventsByProperty) {
  const allBookings = propsSubset.flatMap((p) => (eventsByProperty[p.id] || [])
    .filter((e) => e.extendedProps.type === 'booking')
    .map((e) => ({ ...e, propertyId: p.id, propertyName: p.name })));

  const channelCounts = {};
  allBookings.forEach((b) => {
    const platform = b.extendedProps.platform;
    const channel = platform === 'Airbnb' ? 'airbnb' : platform === 'Booking.com' ? 'booking_com' : 'vrbo';
    channelCounts[channel] = (channelCounts[channel] || 0) + 1;
  });
  const totalBookingCount = allBookings.length;
  const channelMix = Object.entries(channelCounts).map(([source, count]) => ({
    source,
    count,
    pct: totalBookingCount > 0 ? Math.round((count / totalBookingCount) * 100) : 0
  }));

  const now = new Date();
  const upcoming = allBookings
    .filter((b) => new Date(b.start) >= now)
    .sort((a, b) => new Date(a.start) - new Date(b.start))
    .slice(0, 5)
    .map((b) => ({ id: b.id, property_name: b.propertyName, guestName: b.extendedProps.guestName, platform: b.extendedProps.platform, checkIn: b.start, checkOut: b.end }));

  // Omzet: per boeking het aantal nachten x de nachtprijs van HAAR eigen
  // woning (PROPERTY_NIGHTLY_RATES, zelfde volgorde als PROPERTY_NAMES) -
  // voorheen kreeg elke woning dezelfde vaste €95/nacht, waardoor de omzet
  // van elke accommodatie even hoog oogde ongeacht type/grootte.
  const revenueEstimate = allBookings.reduce((sum, b) => {
    const nights = Math.max(1, Math.round((new Date(b.end) - new Date(b.start)) / 86400000));
    const rate = PROPERTY_NIGHTLY_RATES[demoPropertyIndex({ id: b.propertyId }) % PROPERTY_NIGHTLY_RATES.length];
    return sum + nights * rate;
  }, 0);

  // Bezettingsgraad: zelfde formule (rollend venster van 30 dagen terug/
  // vooruit) als het echte /api/automation-overview, hier berekend uit de
  // ECHTE nep-boekingen van propsSubset i.p.v. een losse formule op basis van
  // enkel het aantal woningen - anders klopte de bezetting van 1 woning apart
  // niet met wat je zag bij alle woningen samen (twee onafhankelijke, niet
  // van elkaar afgeleide getallen die toevallig ook nog met elkaar leken te
  // moeten overeenkomen).
  const windowDays = 30;
  const windowStart = new Date(now.getTime() - windowDays * 86400000);
  const windowEnd = new Date(now.getTime() + windowDays * 86400000);
  const bookedNights = allBookings.reduce((sum, b) => {
    const s = new Date(b.start);
    const e = new Date(b.end);
    const clampedStart = s < windowStart ? windowStart : s;
    const clampedEnd = e > windowEnd ? windowEnd : e;
    return sum + Math.max(0, Math.round((clampedEnd - clampedStart) / 86400000));
  }, 0);
  const totalNights = propsSubset.length * windowDays * 2;
  const occupancyRate = totalNights > 0 ? Math.round((bookedNights / totalNights) * 1000) / 10 : 0;

  const reviewsPerProperty = propsSubset.map((p) => {
    const idx = demoPropertyIndex(p);
    return { propertyId: p.id, propertyName: p.name, avg: REVIEW_SCORES[idx % REVIEW_SCORES.length], total: 16 + idx * 9 };
  });
  const reviewsTotal = reviewsPerProperty.reduce((sum, p) => sum + p.total, 0);
  const reviewsAverage = reviewsTotal > 0
    ? Math.round((reviewsPerProperty.reduce((sum, p) => sum + p.avg * p.total, 0) / reviewsTotal) * 10) / 10
    : null;

  return {
    occupancy: { rate: occupancyRate, windowDays: 30 },
    channelMix,
    upcoming,
    revenue: { windowDays: 30, totalsByCurrency: { EUR: revenueEstimate } },
    reviews: { average: reviewsAverage, total: reviewsTotal, perProperty: reviewsPerProperty }
  };
}

const CHANNEL_LABELS = { airbnb: 'Airbnb', booking_com: 'Booking.com', vrbo: 'Vrbo', direct: 'Direct' };

function AutomationPanel({ data, title, filterNode }) {
  const totalChannelCount = data.channelMix.reduce((s, c) => s + c.count, 0);
  return (
    <div className="automation-overview">
      <div className="automation-header"><h2>{title}</h2></div>
      {filterNode}

      <div className="automation-cards">
        <div className="automation-card">
          <div className="automation-card-label">{dt('Omzet')}</div>
          <div className="automation-revenue-values">
            <div className="automation-card-value automation-revenue-value">
              {new Intl.NumberFormat(demoLocale(), { style: 'currency', currency: 'EUR' }).format(data.revenue.totalsByCurrency.EUR)}
            </div>
          </div>
          <div className="automation-card-hint">{dt('Laatste {{n}} dagen', { n: data.revenue.windowDays })}</div>
        </div>

        <div className="automation-card">
          <div className="automation-card-label">{dt('Bezettingsgraad')}</div>
          <div className="automation-card-value">{data.occupancy.rate}%</div>
          <div className="automation-card-hint">{dt('Laatste {{n}} dagen', { n: data.occupancy.windowDays })}</div>
        </div>

        <div className="automation-card automation-card-wide">
          <div className="automation-card-label">{dt('Kanaalmix')}</div>
          {data.channelMix.length === 0 ? (
            <div className="automation-card-hint">{dt('Nog geen boekingen')}</div>
          ) : (
            <div className="automation-channel-bars">
              {data.channelMix.map((c) => {
                const pct = totalChannelCount > 0 ? Math.round((c.count / totalChannelCount) * 100) : 0;
                return (
                  <div className="automation-channel-row" key={c.source}>
                    <span className="automation-channel-name">{CHANNEL_LABELS[c.source] || c.source}</span>
                    <div className="automation-channel-bar-track">
                      <div className="automation-channel-bar-fill" style={{ width: `${pct}%` }}></div>
                    </div>
                    <span className="automation-channel-count">{pct}%</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="automation-section">
        <h3>{dt('Binnenkort')}</h3>
        {data.upcoming.length === 0 ? (
          <p className="automation-card-hint">{dt('Geen komende in-/uitchecks')}</p>
        ) : (
          <ul className="automation-upcoming-list">
            {data.upcoming.map((ev) => (
              <li key={ev.id} className="automation-upcoming-item">
                {ev.property_name && <span className="automation-upcoming-property" title={ev.property_name}>{ev.property_name}</span>}
                <span className="automation-upcoming-guest">
                  <PlatformBadge platform={ev.platform} />
                  <span className="automation-upcoming-guest-name" title={ev.guestName}>{ev.guestName}</span>
                </span>
                <span className="automation-upcoming-dates">{fmtDate(ev.checkIn)} → {fmtDate(ev.checkOut)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="automation-section">
        <h3>{dt('Beoordelingen')}</h3>
        <div className="automation-card-value">{data.reviews.average != null ? `${data.reviews.average.toFixed(1)} / 5` : '—'}</div>
        <div className="automation-card-hint">{dt('Gemiddelde score over {{n}} reviews (Airbnb, Booking.com, ...)', { n: data.reviews.total })}</div>
        {data.reviews.perProperty.length > 1 && (
          <ul className="automation-upcoming-list">
            {data.reviews.perProperty.map((p) => (
              <li key={p.propertyId} className="automation-upcoming-item">
                <span className="automation-upcoming-property">{p.propertyName}</span>
                <span className="automation-upcoming-guest">{p.avg.toFixed(1)} / 5</span>
                <span className="automation-upcoming-dates">{dt('{{n}} reviews', { n: p.total })}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// Nep-versie van het echte CleaningOverview.js: zelfde classnames uit
// CleaningOverview.css en dezelfde opbouw (Aankomend/Voltooid), enkel
// gevoed door de lokale eventsByProperty-state i.p.v. een axios-call naar
// /api/calendar-overview. Omdat de demo geen apart COMPLETED/INVOICED-
// statusveld bijhoudt (enkel ASSIGNED/PENDING_CONFIRMATION, zie
// buildInitialEvents hierboven), wordt "voltooid" hier simpelweg bepaald
// via de einddatum (al voorbij vandaag = voltooid) i.p.v. via de status.
function DemoCleaningOverview({ properties, eventsByProperty, filterNode, sortByProperty = false }) {
  const propertyNameById = Object.fromEntries(properties.map((p) => [p.id, p.name]));
  const now = new Date();

  const allCleanings = properties.flatMap((p) => (eventsByProperty[p.id] || [])
    .filter((e) => e.extendedProps.type === 'cleaning')
    .map((e) => ({ ...e, propertyId: p.id })));

  // Zelfde 7-dagen-limiet als het echte CleaningOverview.js: zonder limiet
  // bleef deze lijst in een langlopende demo-sessie eindeloos doorgroeien.
  const sevenDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

  const upcoming = allCleanings
    .filter((c) => new Date(c.end) >= now)
    .sort((a, b) => new Date(a.start) - new Date(b.start));
  const completed = allCleanings
    .filter((c) => new Date(c.end) < now && new Date(c.extendedProps.arrivedAt || c.start) >= sevenDaysAgo)
    .sort((a, b) => new Date(b.start) - new Date(a.start));

  // Zelfde opbouw als het echte CleaningOverview.js: tabs (Aankomend/Voltooid)
  // op smalle schermen, rijen gegroepeerd per dag met een datumkop, en een
  // gekleurde zijstreep per status (row-*-klassen).
  const [overviewTab, setOverviewTab] = useState('upcoming');
  const refDate = (c, isCompleted) => new Date(isCompleted ? (c.extendedProps.arrivedAt || c.start) : c.start);
  const dateKeyOf = (c, isCompleted) => {
    const d = refDate(c, isCompleted);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const formatDateHeading = (key) => {
    const [y, m, d] = key.split('-').map(Number);
    const text = new Date(y, m - 1, d).toLocaleDateString(demoLocale('NL'), { weekday: 'long', day: 'numeric', month: 'long' });
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const renderRow = (c, isCompleted) => {
    // Zoals in de echte app (verhuurder): enkel blauw (aankomend) of groen (voltooid).
    const statusClass = isCompleted ? 'completed' : 'upcoming';
    return (
      <li className={`cleaning-overview-row row-${statusClass}`} key={c.id}>
        <span className="cleaning-overview-row-main">
          <span className={`cleaning-overview-status-dot ${statusClass}`}></span>
          <span className="cleaning-overview-property">{propertyNameById[c.propertyId] || '—'}</span>
        </span>
        <span className="cleaning-overview-dates">{c.extendedProps.cleanerName || ''}</span>
      </li>
    );
  };

  const renderGrouped = (list, isCompleted) => {
    const groups = [];
    list.forEach((c) => {
      const key = dateKeyOf(c, isCompleted);
      const last = groups[groups.length - 1];
      if (last && last.key === key) last.items.push(c);
      else groups.push({ key, items: [c] });
    });
    // Zoals in de app: binnen dezelfde dag alfabetisch op woning.
    if (sortByProperty) {
      groups.forEach((g) => g.items.sort((x, y) => (propertyNameById[x.propertyId] || '').localeCompare(propertyNameById[y.propertyId] || '', demoLang(), { sensitivity: 'base' })));
    }
    return groups.map((g) => (
      <div className="cleaning-overview-day" key={g.key}>
        <h4 className="cleaning-overview-day-heading">{formatDateHeading(g.key)}</h4>
        <ul className="cleaning-overview-list">{g.items.map((c) => renderRow(c, isCompleted))}</ul>
      </div>
    ));
  };

  return (
    <div className="cleaning-overview">
      <div className="cleaning-overview-header"><h2>{dt('Schoonmaak')}</h2></div>
      {filterNode}

      <div className="cleaning-overview-tabs">
        <button type="button" className={overviewTab === 'upcoming' ? 'active' : ''} onClick={() => setOverviewTab('upcoming')}>{dt('Aankomend')}</button>
        <button type="button" className={overviewTab === 'completed' ? 'active' : ''} onClick={() => setOverviewTab('completed')}><span>{dt('Voltooid')}</span><small className="co-tab-sub">{dt('(laatste 30 dagen)')}</small></button>
      </div>

      <div className="cleaning-overview-columns">
        <div className={`cleaning-overview-section ${overviewTab === 'upcoming' ? '' : 'co-inactive'}`}>
          <h3>{dt('Aankomend')}</h3>
          {upcoming.length === 0 ? (
            <p className="cleaning-overview-empty">{dt('Geen aankomende schoonmaakmomenten.')}</p>
          ) : renderGrouped(upcoming, false)}
        </div>

        <div className={`cleaning-overview-section ${overviewTab === 'completed' ? '' : 'co-inactive'}`}>
          <h3>{dt('Voltooid (laatste 30 dagen)')}</h3>
          {completed.length === 0 ? (
            <p className="cleaning-overview-empty">{dt('Nog geen voltooide schoonmaakmomenten.')}</p>
          ) : renderGrouped(completed, true)}
        </div>
      </div>
    </div>
  );
}

// Nep-versie van het echte PropertyTimeline.js (de "Multi-kalender" die
// verhuurders met meerdere accommodaties echt te zien krijgen): zelfde
// classnames uit PropertyTimeline.css en exact dezelfde
// venster/dagbreedte/balkjes-positioneringslogica, enkel gevoed door de
// lokale eventsByProperty-state hierboven i.p.v. een axios-call naar
// /api/calendar-overview.
function DemoTimeline({ properties, eventsByProperty, onBookingClick, onCleaningClick }) {
  const [viewStart, setViewStart] = useState(defaultTimelineViewStart);
  const [dayWidth, setDayWidth] = useState(() => (
    typeof window !== 'undefined' && window.innerWidth <= 768 ? TIMELINE_MOBILE_PX_PER_DAY : TIMELINE_DESKTOP_PX_PER_DAY
  ));
  const [now, setNow] = useState(() => new Date());
  const scrollRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setDayWidth(window.innerWidth <= 768 ? TIMELINE_MOBILE_PX_PER_DAY : TIMELINE_DESKTOP_PX_PER_DAY);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  const viewEnd = useMemo(() => addDays(viewStart, TIMELINE_WINDOW_DAYS), [viewStart]);

  const days = useMemo(() => {
    const arr = [];
    for (let i = 0; i < TIMELINE_WINDOW_DAYS; i++) arr.push(addDays(viewStart, i));
    return arr;
  }, [viewStart]);

  const monthGroups = useMemo(() => {
    const groups = [];
    days.forEach((day) => {
      const label = day.toLocaleDateString(demoLocale('NL'), { month: 'long', year: 'numeric' });
      const last = groups[groups.length - 1];
      if (last && last.label === label) {
        last.count += 1;
      } else {
        groups.push({ label, count: 1 });
      }
    });
    return groups;
  }, [days]);

  const todayKey = new Date().toDateString();

  useEffect(() => {
    if (!scrollRef.current) return;
    const dayMs = 24 * 60 * 60 * 1000;
    const todayOffsetDays = Math.round((startOfDay(new Date()) - viewStart) / dayMs);
    scrollRef.current.scrollLeft = (todayOffsetDays >= 0 && todayOffsetDays < TIMELINE_WINDOW_DAYS)
      ? todayOffsetDays * dayWidth
      : 0;
  }, [viewStart, dayWidth]);

  const getOffsetPx = useCallback((date) => {
    const hours = (new Date(date) - viewStart) / (60 * 60 * 1000);
    return hours * (dayWidth / 24);
  }, [viewStart, dayWidth]);

  const computeBarStyle = useCallback((evStart, evEnd) => {
    const s = new Date(evStart);
    const e = new Date(evEnd);
    const clampedStart = s < viewStart ? viewStart : s;
    const clampedEnd = e > viewEnd ? viewEnd : e;
    const left = getOffsetPx(clampedStart);
    const width = Math.max(getOffsetPx(clampedEnd) - left, TIMELINE_MIN_BAR_WIDTH_PX);
    return { left, width };
  }, [viewStart, viewEnd, getOffsetPx]);

  const nowLinePx = (now >= viewStart && now <= viewEnd) ? getOffsetPx(now) : null;

  const goPrev = () => setViewStart((prev) => addDays(prev, -TIMELINE_WINDOW_DAYS));
  const goNext = () => setViewStart((prev) => addDays(prev, TIMELINE_WINDOW_DAYS));
  const goToday = () => setViewStart(defaultTimelineViewStart());

  return (
    <div className="property-timeline">
      <div className="timeline-toolbar">
        <div className="rbc-btn-group">
          <button type="button" onClick={goPrev}>{dt('Vorige')}</button>
          <button type="button" onClick={goToday}>{dt('Vandaag')}</button>
          <button type="button" onClick={goNext}>{dt('Volgende')}</button>
        </div>
      </div>

      <div className="timeline-body">
        <div className="timeline-labels-col">
          <div className="timeline-label-col timeline-label-col-header" />
          {properties.map((property) => (
            <div className="timeline-label-col" key={property.id}>
              <img className="timeline-property-photo" src={property.photoUrl} alt="" />
              <span className="timeline-property-text">
                <span className="timeline-property-name">{property.name}</span>
              </span>
            </div>
          ))}
        </div>

        <div className="timeline-scroll" ref={scrollRef}>
          <div className="timeline-header-columns" style={{ width: TIMELINE_WINDOW_DAYS * dayWidth }}>
            <div className="timeline-months-header">
              {monthGroups.map((group, idx) => (
                <div key={`${group.label}-${idx}`} className="timeline-month-label" style={{ width: group.count * dayWidth }}>
                  {group.label}
                </div>
              ))}
            </div>
            <div className="timeline-days-header">
              {days.map((day) => (
                <div
                  key={day.toISOString()}
                  className={`timeline-day-label ${day.toDateString() === todayKey ? 'is-today' : ''}`}
                  style={{ width: dayWidth }}
                >
                  <span className="timeline-day-weekday">{day.toLocaleDateString(demoLocale('NL'), { weekday: 'short' })}</span>
                  <span className="timeline-day-number">{day.getDate()}</span>
                </div>
              ))}
            </div>
          </div>

          {properties.map((property) => {
            const events = eventsByProperty[property.id] || [];
            // Zelfde fix als het echte PropertyTimeline.js: enkel events die
            // dit venster ook echt overlappen mogen als balkje getekend
            // worden, anders klemt computeBarStyle een volledig-buiten-beeld
            // event vast op links (spook-balkje op dag 1 na Vorige/Volgende).
            const bookings = events
              .filter((e) => e.extendedProps.type === 'booking')
              .filter((e) => new Date(e.end) > viewStart && new Date(e.start) < viewEnd);
            const cleanings = events
              .filter((e) => e.extendedProps.type === 'cleaning')
              .filter((e) => new Date(e.end) > viewStart && new Date(e.start) < viewEnd);

            return (
              <div className="timeline-track" style={{ width: TIMELINE_WINDOW_DAYS * dayWidth }} key={property.id}>
                {days.map((day, idx) => (
                  <div
                    key={day.toISOString()}
                    className={`timeline-gridline ${day.toDateString() === todayKey ? 'is-today' : ''}`}
                    style={{ left: idx * dayWidth, width: dayWidth }}
                  />
                ))}

                {nowLinePx !== null && (
                  <div className="timeline-now-line" style={{ left: nowLinePx }} />
                )}

                {bookings.map((booking) => {
                  const { left, width } = computeBarStyle(booking.start, booking.end);
                  return (
                    <div
                      key={`booking-${booking.id}`}
                      className="timeline-bar timeline-bar-booking"
                      style={{ left, width }}
                      title={`${booking.extendedProps.guestName}\n${fmtDateTime(booking.start)} → ${fmtDateTime(booking.end)}`}
                      onClick={() => onBookingClick(property, booking)}
                    >
                      <span className="timeline-bar-label">
                        <PlatformBadge platform={booking.extendedProps.platform} className="platform-badge-only" />
                      </span>
                    </div>
                  );
                })}

                {cleanings.map((cleaning) => {
                  const { left, width } = computeBarStyle(cleaning.start, cleaning.end);
                  // 3 kleuren i.p.v. 2, zelfde als het echte PropertyTimeline.js:
                  // rood zolang er nog geen schoonmaker toegewezen is
                  // (Ingepland), oranje zodra die er wel is maar de schoonmaak
                  // nog niet voltooid is (Toegewezen), groen zodra voltooid of
                  // al verstreken (Voltooid).
                  const isPast = new Date(cleaning.end) < now;
                  const isCompleted = isPast || ['COMPLETED', 'INVOICED'].includes(cleaning.extendedProps.status);
                  // Zoals in de echte app (verhuurder): enkel blauw (aankomend) of groen (voltooid).
                  const cleaningStatusClass = isCompleted ? 'timeline-bar-cleaning-completed' : 'timeline-bar-cleaning-assigned';
                  return (
                    <div
                      key={`cleaning-${cleaning.id}`}
                      className={`timeline-bar timeline-bar-cleaning ${cleaningStatusClass}`}
                      style={{ left, width }}
                      title={`Schoonmaak\n${fmtDateTime(cleaning.start)} → ${fmtDateTime(cleaning.end)}`}
                      onClick={() => onCleaningClick(property, cleaning)}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
}

// Bouwt de nep-meldingen uit dezelfde berichtformaten als de echte backend
// (zie de createNotification-aanroepen in index.js voor CHECKIN/
// BATTERY_WARNING/CLEANER_ARRIVED), gekoppeld aan een echte demo-woning/gast/
// schoonmaker i.p.v. losstaande generieke tekst - zo oogt dit ook echt zoals
// de echte meldingen, en kan doorklikken (net als NotificationBell.js met
// onNotificationClick in het echte Dashboard.js) naar de juiste woning + tab
// navigeren i.p.v. nergens naartoe te gaan.
//
// Net als in de echte app (backend GET /api/notification-preferences en de plekken
// waar de meldingen verstuurd worden) hangen de meldingen af van de gekozen diensten:
// - slot-meldingen (gast ingecheckt, batterij) enkel bij toegangsbeheer;
// - "schoonmaker toegekomen" enkel bij toegangsbeheer EN schoonmaak (komt uit het ontgrendelen);
// - "schoonmaak voltooid" bij schoonmaak zonder toegangsbeheer;
// - automation geeft geen meldingen.
function buildDemoNotifications(properties, eventsByProperty = {}) {
  const hasCleaning = (p) => p.cleaningTier !== 'NONE';
  const lockProps = properties.filter((p) => p.accessManagementEnabled);
  const lockCleaningProp = lockProps.find(hasCleaning);
  const cleaningOnlyProp = properties.find((p) => hasCleaning(p) && !p.accessManagementEnabled);
  const list = [];
  if (lockProps.length > 0) {
    const p0 = lockProps[0];
    const firstBooking = (eventsByProperty[p0.id] || []).find((e) => e.extendedProps.type === 'booking');
    list.push({
      id: 1,
      type: 'CHECKIN',
      relatedId: p0.id,
      message: dt('{{name}} is ingecheckt bij {{prop}}.', { name: firstBooking?.extendedProps.guestName || dt('Je gast'), prop: p0.name }),
      time: dt('2 uur geleden')
    });
    // De tweede woning heeft in de demo bewust een zwakke batterij (34%, zie batteryByProperty).
    const p1 = lockProps[1];
    if (p1) {
      list.push({
        id: 2,
        type: 'BATTERY_WARNING',
        relatedId: p1.id,
        message: dt('Batterijniveau slim slot {{prop}}: 34%. Laad binnenkort op.', { prop: p1.name }),
        time: dt('gisteren')
      });
    }
  }
  if (lockCleaningProp) {
    list.push({
      id: 3,
      type: 'CLEANER_ARRIVED',
      relatedId: lockCleaningProp.id,
      message: dt('Schoonmaker is toegekomen bij {{prop}}.', { prop: lockCleaningProp.name || lockCleaningProp.address }),
      time: dt('2 dagen geleden')
    });
  }
  if (cleaningOnlyProp) {
    list.push({
      id: 4,
      type: 'CLEANING_COMPLETED',
      relatedId: cleaningOnlyProp.id,
      message: dt('Schoonmaak voltooid bij {{prop}}.', { prop: cleaningOnlyProp.name || cleaningOnlyProp.address }),
      time: dt('2 dagen geleden')
    });
  }
  return list;
}

function DemoNotificationBell({ properties, eventsByProperty, onNotificationClick, screen = false, onScreenOpen }) {
  const [open, setOpen] = useState(false);
  const [readIds, setReadIds] = useState([]);
  const containerRef = useRef(null);
  useEffect(() => {
    if (screen && onScreenOpen) onScreenOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const notifications = useMemo(() => buildDemoNotifications(properties, eventsByProperty), [properties, eventsByProperty]);

  const unreadCount = notifications.filter((n) => !readIds.includes(n.id)).length;

  const handleClick = (n) => {
    setReadIds((prev) => (prev.includes(n.id) ? prev : [...prev, n.id]));
    onNotificationClick(n);
    setOpen(false);
  };

  const markAllRead = () => setReadIds(notifications.map((n) => n.id));

  return (
    <div className={screen ? '' : 'notification-bell-container'} ref={containerRef}>
      {!screen && (
      <button className="notification-bell-btn" onClick={() => setOpen(!open)} aria-label={dt('Meldingen')}>
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
      </button>
      )}
      {(open || screen) && (
        <div className={screen ? 'notification-screen' : 'notification-dropdown'}>
          <div className="notification-dropdown-header">
            <span>{dt('Meldingen')}</span>
            {!screen && unreadCount > 0 && (
              <button className="notification-mark-all" onClick={markAllRead}>{dt('Alles gelezen')}</button>
            )}
          </div>
          <div className="notification-list">
            {notifications.length === 0 && <div className="notification-empty">{dt('Geen meldingen')}</div>}
            {notifications.map((n) => {
              const severityClass = n.type === 'BATTERY_LOW'
                ? 'notification-critical'
                : n.type === 'BATTERY_WARNING'
                ? 'notification-warning'
                : '';
              const isRead = readIds.includes(n.id);
              return (
                <div
                  key={n.id}
                  className={`notification-item ${isRead ? '' : 'unread'} ${severityClass} clickable`}
                  onClick={() => handleClick(n)}
                >
                  <p className="notification-message">{n.message}</p>
                  <div className="notification-footer">
                    <span className="notification-time">{n.time}</span>
                    <svg className="notification-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function DemoProfileMenu({ onLogoutClick, screen = false, onOpenProperties }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={screen ? '' : 'profile-menu-container'}>
      {!screen && (
      <button className="profile-menu-btn" onClick={() => setOpen(!open)} aria-label="Profiel">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="8" r="4" />
          <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
        </svg>
      </button>
      )}
      {(open || screen) && (
        <div className={screen ? 'profile-menu-dropdown profile-menu-screen' : 'profile-menu-dropdown'}>
          <div className="profile-menu-name">Demo klant</div>
          <div className="profile-menu-divider" />
          {onOpenProperties && (
            <button onClick={() => { onOpenProperties(); setOpen(false); }}>Accommodaties</button>
          )}
          {onOpenProperties && <div className="profile-menu-divider" />}
          <button onClick={() => { onLogoutClick(); setOpen(false); }}>Uitloggen</button>
        </div>
      )}
    </div>
  );
}

// Kalender-tabblad in de telefoonweergave, zoals CalendarHub.js in de echte app:
// schakelaar Multi-kalender / Maandkalender, met een woningfilter erboven.
function DemoCalendarHub({ properties, eventsByProperty, onBookingClick, onCleaningClick, renderMonth }) {
  const [mode, setMode] = useState('multi');
  const multi = usePropertySelection(null, properties, false);
  const month = usePropertySelection(null, properties, true);
  const monthProperty = month.selectedProperties[0];
  return (
    <div className="cal-hub">
      <div className="cal-hub-toggle" role="tablist">
        <button type="button" role="tab" aria-selected={mode === 'multi'} className={mode === 'multi' ? 'active' : ''} onClick={() => setMode('multi')}>{dt('Multi-kalender')}</button>
        <button type="button" role="tab" aria-selected={mode === 'month'} className={mode === 'month' ? 'active' : ''} onClick={() => setMode('month')}>{dt('Maandkalender')}</button>
      </div>
      {mode === 'multi' ? (
        <>
          <PropertyFilter properties={properties} selected={multi.selected} onChange={multi.setSelected} />
          <DemoTimeline
            properties={multi.selectedProperties}
            eventsByProperty={eventsByProperty}
            onBookingClick={onBookingClick}
            onCleaningClick={onCleaningClick}
          />
        </>
      ) : (
        <>
          <PropertyFilter properties={properties} selected={month.selected} onChange={month.setSelected} single />
          {monthProperty && renderMonth(monthProperty)}
        </>
      )}
    </div>
  );
}

// Schoonmaak- en Automation-tabblad in de telefoonweergave: met woningfilter.
function DemoFilteredCleaning({ properties, eventsByProperty }) {
  const sel = usePropertySelection(null, properties, false);
  return (
    <DemoCleaningOverview
      properties={sel.selectedProperties}
      eventsByProperty={eventsByProperty}
      sortByProperty
      filterNode={<PropertyFilter properties={properties} selected={sel.selected} onChange={sel.setSelected} />}
    />
  );
}

function DemoFilteredAutomation({ properties, eventsByProperty }) {
  const sel = usePropertySelection(null, properties, false);
  const filterNode = <PropertyFilter properties={properties} selected={sel.selected} onChange={sel.setSelected} />;
  // Niets geselecteerd (bv. na "Alles verwijderen"): enkel de titel + filter
  // tonen, zodat je meteen weer woningen kan kiezen - zoals in de echte app.
  if (sel.selected.length === 0) {
    return (
      <div className="automation-overview">
        <div className="automation-header"><h2>SmartStays Automation</h2></div>
        {filterNode}
      </div>
    );
  }
  return (
    <AutomationPanel
      data={buildAutomationData(sel.selectedProperties, eventsByProperty)}
      title="SmartStays Automation"
      filterNode={filterNode}
    />
  );
}

function DemoDashboard() {
  useTranslation(); // her-render bij taalwissel (dt() leest i18n.language)
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const access = searchParams.get('access') !== '0';
  const automation = searchParams.get('automation') === '1';
  const tier = searchParams.get('tier') || 'FULL';
  const count = Math.min(6, Math.max(1, parseInt(searchParams.get('count'), 10) || 2));

  const [properties, setProperties] = useState(() => buildDemoProperties(count, access, automation, tier));
  const [mainView, setMainView] = useState('multi-calendar');
  const [selectedPropertyId, setSelectedPropertyId] = useState(properties[0]?.id || null);
  const [activeTab, setActiveTab] = useState(access ? 'smartlock' : 'calendar');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Telefoonweergave = zoals de echte mobiele app: onderste tabbalk i.p.v. menu/belletje/profiel bovenaan.
  // Actief in de telefoon-mockup (embedded) en op een echte telefoon.
  const [phoneView, setPhoneView] = useState(() => searchParams.get('embedded') === '1' || window.innerWidth <= 820);
  useEffect(() => {
    const onResize = () => setPhoneView(searchParams.get('embedded') === '1' || window.innerWidth <= 820);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [searchParams]);
  const [nativePropertyDetail, setNativePropertyDetail] = useState(false);
  const [demoUnread, setDemoUnread] = useState(() => buildDemoNotifications(properties).length);
  // Slotje-tab (zoals in de echte app) enkel met toegangsbeheer; zonder is er
  // geen woningenlijst meer in de tabbalk (die staat onder Profiel > Accommodaties).
  const lockProperties = properties.filter((p) => p.accessManagementEnabled);
  const hasLock = access && lockProperties.length > 0;
  useEffect(() => {
    if (!phoneView || mainView !== 'properties' || nativePropertyDetail) return;
    if (lockProperties.length === 1) {
      setSelectedPropertyId(lockProperties[0].id);
      setActiveTab('smartlock');
      setNativePropertyDetail(true);
    }
  }, [phoneView, mainView, nativePropertyDetail, lockProperties.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const [eventsByProperty, setEventsByProperty] = useState(() => Object.fromEntries(properties.map((p, i) => [p.id, buildInitialEvents(i, access, tier)])));
  const [lockedByProperty, setLockedByProperty] = useState(() => Object.fromEntries(properties.map((p) => [p.id, true])));
  // Batterijpercentage blijft in de demo bewust vast (geen echt slot dat
  // effectief verbruikt) - (ont)grendelen mag dit dus niet laten dalen, in
  // tegenstelling tot een eerdere versie hiervan.
  // Woning op index 1 (Appartement Zeezicht) krijgt bewust een laag,
  // hardcoded percentage: de BATTERY_WARNING-melding hieronder verwijst naar
  // exact deze woning en moet met dit getal overeenkomen (zie de
  // batterijmelding-tekst verderop, die er niet los van mag staan).
  const [batteryByProperty] = useState(() => Object.fromEntries(properties.map((p, i) => [p.id, i === 1 ? 34 : Math.max(45, 88 - i * 9)])));
  const [codesByProperty, setCodesByProperty] = useState(() => Object.fromEntries(properties.map((p) => [p.id, buildInitialCodes()])));
  const [logByProperty] = useState(() => Object.fromEntries(properties.map((p) => [p.id, buildFakeLog(eventsByProperty[p.id], codesByProperty[p.id])])));
  const [showSmartlockLog, setShowSmartlockLog] = useState(false);
  // Zelfde curatie als de echte Dashboard.js: standaard enkel de acties met
  // een naam tonen (gast/schoonmaker/andere persoonlijke code), de rest komt
  // enkel in de volledige geschiedenis hieronder terecht.
  const [showFullSmartlockLog, setShowFullSmartlockLog] = useState(false);

  const [showEditPropertyModal, setShowEditPropertyModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);

  // Eerste dag van de zichtbare maand voor de per-woning kalender -
  // vervangt FullCalendar's eigen interne view-state (zie PropertyCalendar.js
  // voor de echte, niet-demo variant van deze zelfgebouwde maandweergave).
  const [monthAnchor, setMonthAnchor] = useState(() => startOfMonth(new Date()));
  const [showCleaningModal, setShowCleaningModal] = useState(false);
  const [cleaningModalPropertyId, setCleaningModalPropertyId] = useState('');
  const [selectedBookingId, setSelectedBookingId] = useState('');
  const [cleaningForm, setCleaningForm] = useState({ startDate: new Date(), endDate: new Date(Date.now() + 2 * 60 * 60 * 1000) });
  const [bookingModalData, setBookingModalData] = useState(null);

  const [showAddCodeModal, setShowAddCodeModal] = useState(false);
  const [showEditCodeModal, setShowEditCodeModal] = useState(false);
  const [editingCodeId, setEditingCodeId] = useState(null);
  const [codeForm, setCodeForm] = useState({ name: '', code: '', confirm: '' });
  const [codeError, setCodeError] = useState('');

  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  // Eigen bevestigingsmodaltje i.p.v. window.confirm() - askConfirm() vult
  // confirmState.message/onConfirm, zie de JSX + helper verderop.
  const [confirmState, setConfirmState] = useState(null);

  // Valt terug op de eerste woning (en nooit op "niets"), zodat het scherm
  // niet leeg kan raken als de gekozen woning om welke reden ook verdwijnt.
  const selectedProperty = properties.find((p) => p.id === selectedPropertyId) || properties[0] || null;
  const showSmartlockTab = access && !!selectedProperty?.accessManagementEnabled;
  const showAutomationTab = automation && !!selectedProperty?.automationEnabled;
  const locked = lockedByProperty[selectedPropertyId];
  const battery = batteryByProperty[selectedPropertyId] || 0;
  const log = logByProperty[selectedPropertyId] || [];
  const importantLog = log.filter((entry) => entry.name);
  const codes = codesByProperty[selectedPropertyId] || [];
  const events = eventsByProperty[selectedPropertyId] || [];

  // Eigen maandweergave voor de per-woning "Kalender"-tab (zie het
  // uitgebreide commentaar in PropertyCalendar.css bij ".month-view" voor
  // waarom - zelfde opbouw als de echte, niet-demo PropertyCalendar.js).
  const calendarTitle = monthAnchor.toLocaleDateString(demoLocale('NL'), { month: 'long', year: 'numeric' });
  const goPrevMonth = () => setMonthAnchor((prev) => addMonths(prev, -1));
  const goNextMonth = () => setMonthAnchor((prev) => addMonths(prev, 1));
  const goCurrentMonth = () => setMonthAnchor(startOfMonth(new Date()));
  const weekdayLabels = (() => {
    const referenceMonday = new Date(2026, 0, 12);
    return Array.from({ length: 7 }, (_, i) => addDays(referenceMonday, i).toLocaleDateString(demoLocale('NL'), { weekday: 'short' }));
  })();
  const calendarWeeks = (() => {
    const monthStart = startOfMonth(monthAnchor);
    const monthEndExclusive = addMonths(monthStart, 1);
    const lastDayOfMonth = addDays(monthEndExclusive, -1);
    const gridStart = addDays(monthStart, -mondayIndex(monthStart));
    const gridEndExclusive = addDays(lastDayOfMonth, 7 - mondayIndex(lastDayOfMonth));
    const result = [];
    for (let cursor = gridStart; cursor < gridEndExclusive; cursor = addDays(cursor, 7)) {
      result.push(cursor);
    }
    return result;
  })();

  // Niet-ingeplande boekingen van de woning die momenteel in de
  // schoonmaak-modal geselecteerd staat (kan een andere woning zijn dan
  // selectedPropertyId, bv. wanneer de modal via de Multi-kalender geopend
  // werd voor een woning die nog niet "geselecteerd" is in de sidebar).
  const unscheduledForModal = (eventsByProperty[cleaningModalPropertyId] || [])
    .filter((e) => e.extendedProps.type === 'booking' && !e.extendedProps.hasCleaning)
    .map((e) => ({ id: e.id, guestName: e.extendedProps.guestName, checkIn: e.start, checkOut: e.end }));

  const now = new Date();
  const activeBooking = events.find((e) => e.extendedProps.type === 'booking' && new Date(e.start) <= now && new Date(e.end) >= now);
  const currentGuestCode = activeBooking ? { active: true, code: '583147', guestName: activeBooking.extendedProps.guestName } : { active: false };

  const showToast = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
  };

  // "Uitloggen" in de demo: terug naar het menu waar de demo gestart werd. Een ingelogde
  // admin komt zo weer in het beheerpaneel (tabblad Demo, dat onthoudt zijn laatste tab);
  // een bezoeker via een gedeelde demolink gaat naar de startpagina.
  const handleDemoLogout = () => {
    let role = null;
    try { role = JSON.parse(getAuthUser() || 'null')?.role; } catch (e) { role = null; }
    navigate(role === 'ADMIN' && getAuthToken() ? '/admin' : '/');
  };

  const askConfirm = (message, onConfirm) => setConfirmState({ message, onConfirm });

  const selectProperty = (prop) => {
    setMainView('properties');
    setNativePropertyDetail(true);
    setSelectedPropertyId(prop.id);
    // Zelfde volgorde/zichtbaarheid als de tab-knoppen hieronder (Automation,
    // Kalender, Smart lock, Codes) - dus de EERSTE zichtbare tab voor déze
    // woning, net als in het echte Dashboard.js. Kalender staat altijd vóór
    // Smart lock/Codes, dus enkel Automation (indien ingeschakeld) gaat hier
    // vóór Kalender.
    setActiveTab(phoneView ? 'smartlock' : (automation && prop.automationEnabled ? 'automation' : 'calendar'));
    setShowSmartlockLog(false);
  };

  // Zelfde navigatielogica als onNotificationClick in het echte Dashboard.js:
  // batterijmeldingen gaan naar de Smart lock-tab, de rest naar de kalender.
  const handleNotificationClick = (n) => {
    const relatedProperty = properties.find((p) => p.id === n.relatedId);
    if (!relatedProperty) return;
    if (phoneView) {
      // App: batterijmelding -> Smart lock van die woning, de rest -> Kalender.
      if (n.type === 'BATTERY_WARNING' || n.type === 'BATTERY_LOW') {
        setMainView('properties');
        setNativePropertyDetail(true);
        setSelectedPropertyId(relatedProperty.id);
        setActiveTab('smartlock');
      } else {
        setMainView('multi-calendar');
      }
      return;
    }
    setMainView('properties');
    setNativePropertyDetail(true);
    setSelectedPropertyId(relatedProperty.id);
    setActiveTab(n.type === 'BATTERY_WARNING' || n.type === 'BATTERY_LOW' ? 'smartlock' : 'calendar');
  };

  const openEditPropertyModal = (prop) => {
    setEditingProperty({ id: prop.id, name: prop.name, address: prop.address || '', photoUrl: prop.photoUrl || null });
    setShowEditPropertyModal(true);
  };

  const handleEditPropertySave = (e) => {
    e.preventDefault();
    if (!validateRequiredFields(e.target)) {
      showToast('Vul alle verplichte velden in', 'error');
      return;
    }
    setProperties((prev) => prev.map((p) => (p.id === editingProperty.id ? { ...p, name: editingProperty.name, address: editingProperty.address } : p)));
    setShowEditPropertyModal(false);
    showToast('Woning bijgewerkt (demo)', 'success');
  };

  const handleLockAction = (shouldLock) => {
    setLockedByProperty((prev) => ({ ...prev, [selectedPropertyId]: shouldLock }));
    // Geen "(demo)"-suffix hier (anders dan de meeste andere demo-acties
    // hieronder): ont-/vergrendelen is net de kernactie die een bezoeker
    // wil uitproberen, en moet dus zo overtuigend/echt mogelijk aanvoelen.
    showToast(shouldLock ? 'Deur vergrendeld' : 'Deur ontgrendeld', 'success');
  };

  const handleSyncSmartlock = () => {
    showToast('Smart lock gesynchroniseerd', 'success');
  };

  const validateCode = (code) => {
    if (!/^[1-9]{6}$/.test(code) || code.startsWith('12')) {
      return 'Code moet 6 cijfers zijn (1-9, geen 0) en niet starten met "12"';
    }
    return null;
  };

  const resetCodeForm = () => {
    setCodeForm({ name: '', code: '', confirm: '' });
    setCodeError('');
    setEditingCodeId(null);
  };

  const openAddCodeModal = () => { resetCodeForm(); setShowAddCodeModal(true); };
  const openEditCodeModal = (code) => {
    setCodeForm({ name: code.name, code: code.code, confirm: code.code });
    setEditingCodeId(code.id);
    setCodeError('');
    setShowEditCodeModal(true);
  };

  const handleAddCode = () => {
    const error = validateCode(codeForm.code);
    if (error) { setCodeError(error); return; }
    if (codeForm.code !== codeForm.confirm) { setCodeError('Codes komen niet overeen'); return; }
    if (codes.length >= 4) { setCodeError('Maximum 4 codes per woning'); return; }
    const newCode = { id: Date.now(), name: codeForm.name || `Code ${codes.length + 1}`, code: codeForm.code };
    setCodesByProperty((prev) => ({ ...prev, [selectedPropertyId]: [...(prev[selectedPropertyId] || []), newCode] }));
    setShowAddCodeModal(false);
    resetCodeForm();
    showToast('Code toegevoegd (demo)', 'success');
  };

  const handleEditCode = () => {
    const error = validateCode(codeForm.code);
    if (error) { setCodeError(error); return; }
    if (codeForm.code !== codeForm.confirm) { setCodeError('Codes komen niet overeen'); return; }
    setCodesByProperty((prev) => ({
      ...prev,
      [selectedPropertyId]: (prev[selectedPropertyId] || []).map((c) => (c.id === editingCodeId ? { ...c, name: codeForm.name || c.name, code: codeForm.code } : c))
    }));
    setShowEditCodeModal(false);
    resetCodeForm();
    showToast('Code bijgewerkt (demo)', 'success');
  };

  const handleDeleteCode = (codeId, codeName) => {
    askConfirm(`"${codeName}" verwijderen?`, () => {
      setCodesByProperty((prev) => ({ ...prev, [selectedPropertyId]: (prev[selectedPropertyId] || []).filter((c) => c.id !== codeId) }));
      showToast('Code verwijderd (demo)', 'success');
    });
  };

  const defaultCleaningDurationFor = (propertyId) => ((properties.find((p) => p.id === propertyId)?.cleaningHours) || 2) * 60 * 60 * 1000;

  const resetCleaningForm = () => {
    setShowCleaningModal(false);
    setCleaningModalPropertyId('');
    setSelectedBookingId('');
  };

  // propertyId: welke woning de modal target (kan afwijken van
  // selectedPropertyId - zie hierboven bij unscheduledForModal). Bij meer dan
  // 1 woning en een open oproep vanuit de Multi-kalender (properties.length >
  // 1, geen specifieke woning aangeklikt) start dit leeg, exact zoals het
  // echte PropertyTimeline.js: de gebruiker kiest dan eerst een woning in de
  // modal zelf.
  const handleOpenCleaningModal = (propertyId = '', bookingId = '') => {
    setCleaningModalPropertyId(propertyId);
    setSelectedBookingId(bookingId);
    const propEvents = eventsByProperty[propertyId] || [];
    const booking = bookingId ? propEvents.find((e) => e.id === bookingId) : null;
    if (booking) {
      const start = new Date(booking.end);
      start.setHours(start.getHours() + 1);
      setCleaningForm({ startDate: start, endDate: new Date(start.getTime() + defaultCleaningDurationFor(propertyId)) });
    } else {
      const start = new Date();
      setCleaningForm({ startDate: start, endDate: new Date(start.getTime() + defaultCleaningDurationFor(propertyId)) });
    }
    setShowCleaningModal(true);
  };

  const handleSelectCleaningModalProperty = (propertyId) => {
    setCleaningModalPropertyId(propertyId);
    setSelectedBookingId('');
    const start = new Date();
    setCleaningForm({ startDate: start, endDate: new Date(start.getTime() + defaultCleaningDurationFor(propertyId)) });
  };

  const handleSelectBooking = (bookingId) => {
    setSelectedBookingId(bookingId);
    const booking = unscheduledForModal.find((b) => b.id === bookingId);
    if (booking) {
      const start = new Date(booking.checkOut);
      start.setHours(start.getHours() + 1);
      setCleaningForm({ startDate: start, endDate: new Date(start.getTime() + defaultCleaningDurationFor(cleaningModalPropertyId)) });
    }
  };

  const handleSaveCleaning = (e) => {
    e.preventDefault();
    if (!validateRequiredFields(e.target)) {
      showToast('Vul alle verplichte velden in', 'error');
      return;
    }
    if (!cleaningModalPropertyId) {
      showToast('Kies eerst een woning', 'error');
      return;
    }
    if (cleaningForm.startDate >= cleaningForm.endDate) {
      showToast('Einde moet na de start liggen', 'error');
      return;
    }
    const newEvent = {
      id: `c-new-${Date.now()}`,
      title: `Schoonmaak (${fmtTime(cleaningForm.startDate)} → ${fmtTime(cleaningForm.endDate)})`,
      start: cleaningForm.startDate,
      end: cleaningForm.endDate,
      allDay: false,
      backgroundColor: '#ef9a9a',
      borderColor: '#ef9a9a',
      textColor: 'white',
      extendedProps: { type: 'cleaning', status: 'PENDING_CONFIRMATION', cleanerName: null, bookingId: selectedBookingId || null }
    };
    setEventsByProperty((prev) => {
      const list = prev[cleaningModalPropertyId] || [];
      const updated = selectedBookingId
        ? list.map((e) => (e.id === selectedBookingId ? { ...e, extendedProps: { ...e.extendedProps, hasCleaning: true } } : e))
        : list;
      return { ...prev, [cleaningModalPropertyId]: [...updated, newEvent] };
    });
    resetCleaningForm();
    showToast('Schoonmaak ingepland (demo)', 'success');
  };

  const handleDeleteCleaning = (propertyId, cleaningId) => {
    setEventsByProperty((prev) => {
      const list = prev[propertyId] || [];
      const target = list.find((e) => e.id === cleaningId);
      const bookingId = target?.extendedProps?.bookingId;
      const updated = list
        .filter((e) => e.id !== cleaningId)
        .map((e) => (e.id === bookingId ? { ...e, extendedProps: { ...e.extendedProps, hasCleaning: false } } : e));
      return { ...prev, [propertyId]: updated };
    });
    showToast('Schoonmaaktaak verwijderd (demo)', 'success');
  };

  const openBookingModal = (property, event) => {
    const props = event.extendedProps;
    setBookingModalData({
      id: event.id,
      propertyId: property.id,
      propertyName: property.name,
      guestName: props.guestName,
      guestEmail: props.guestEmail,
      platform: props.platform,
      checkIn: event.start,
      checkOut: event.end,
      hasNoCleaning: !props.hasCleaning,
      cleaningTier: property.cleaningTier
    });
  };

  const confirmDeleteCleaning = (property, event) => {
    const props = event.extendedProps;
    const info = `Schoonmaak\n${fmtDateTime(event.start)} - ${fmtDateTime(event.end)}\nStatus: ${props.status === 'ASSIGNED' ? 'Toegewezen' : 'Ingepland'}${props.cleanerName ? `\nSchoonmaker: ${props.cleanerName}` : ''}`;
    askConfirm(`${info}\n\nDeze taak verwijderen?`, () => handleDeleteCleaning(property.id, event.id));
  };

  // Maandweergave van 1 woning (tab Kalender op desktop; Maandkalender in de app).
  const renderMonthCalendar = (property) => {
    const propEvents = eventsByProperty[property.id] || [];
    return (
                <div className="property-calendar">
                  <div className="calendar-header">
                    <h3>Kalender - {property.name}</h3>
                  </div>

                  <div className="rbc-toolbar">
                    <div className="rbc-btn-group">
                      <button type="button" onClick={goPrevMonth}>{dt('Vorige')}</button>
                      <button type="button" onClick={goCurrentMonth}>{dt('Vandaag')}</button>
                      <button type="button" onClick={goNextMonth}>{dt('Volgende')}</button>
                    </div>
                    <div className="rbc-toolbar-label" onClick={goCurrentMonth}>{calendarTitle}</div>
                  </div>

                  {/* Eigen maandweergave i.p.v. FullCalendar - zie
                      PropertyCalendar.css bij ".month-view" voor de reden
                      (geen "+N meer"-overflow, geen dubbel tijd-label). */}
                  <div className="month-view">
                    <div className="month-weekday-header">
                      {weekdayLabels.map((label) => (
                        <div key={label} className="month-weekday-label">{label}</div>
                      ))}
                    </div>

                    {calendarWeeks.map((weekStart) => {
                      const weekEnd = addDays(weekStart, 7);
                      const weekBookings = propEvents.filter((ev) => ev.extendedProps.type === 'booking'
                        && new Date(ev.end) > weekStart && new Date(ev.start) < weekEnd);
                      const weekCleanings = propEvents.filter((ev) => ev.extendedProps.type === 'cleaning'
                        && new Date(ev.end) > weekStart && new Date(ev.start) < weekEnd);

                      const barStyle = (evStart, evEnd) => {
                        const s = new Date(evStart);
                        const e = new Date(evEnd);
                        const clampedStart = s < weekStart ? weekStart : s;
                        const clampedEnd = e > weekEnd ? weekEnd : e;
                        const weekMs = 7 * 24 * 60 * 60 * 1000;
                        const leftPct = ((clampedStart - weekStart) / weekMs) * 100;
                        const rawWidthPct = ((clampedEnd - clampedStart) / weekMs) * 100;
                        return { left: `${leftPct}%`, width: `${Math.max(rawWidthPct, 1.5)}%` };
                      };

                      return (
                        <div className="month-week-row" key={weekStart.toISOString()}>
                          <div className="month-week-daynums">
                            {Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)).map((day) => (
                              <div
                                key={day.toISOString()}
                                className={`month-week-daynum ${day.getMonth() === monthAnchor.getMonth() ? 'is-current-month' : ''} ${day.toDateString() === now.toDateString() ? 'is-today' : ''}`}
                              >
                                {day.getDate()}
                              </div>
                            ))}
                          </div>

                          <div className="month-week-track">
                            {Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)).map((day, idx) => (
                              <div
                                key={day.toISOString()}
                                className={`month-week-gridline ${day.toDateString() === now.toDateString() ? 'is-today' : ''}`}
                                style={{ left: `${(idx / 7) * 100}%`, width: `${(1 / 7) * 100}%` }}
                              />
                            ))}

                            {weekBookings.map((booking) => {
                              const { left, width } = barStyle(booking.start, booking.end);
                              return (
                                <div
                                  key={`booking-${booking.id}`}
                                  className={`month-bar month-bar-booking ${/vrbo|homeaway/i.test(String(booking.extendedProps.platform || '')) ? 'is-wide' : ''}`}
                                  style={{ left: `min(${left}, calc(100% - var(--min-booking-bar)))`, width }}
                                  title={`${booking.extendedProps.guestName}${booking.extendedProps.platform ? ` - ${booking.extendedProps.platform}` : ''}\n${fmtDateTime(booking.start)} → ${fmtDateTime(booking.end)}`}
                                  onClick={() => openBookingModal(property, booking)}
                                >
                                  {/* Logo van het boekingskanaal, enkel op de rij waar de check-in valt (zoals in de echte app). */}
                                  {new Date(booking.start) >= weekStart && (
                                    <PlatformBadge platform={booking.extendedProps.platform} className="platform-badge-only" />
                                  )}
                                </div>
                              );
                            })}

                            {weekCleanings.map((cleaning) => {
                              // 3 kleuren i.p.v. 2: rood zolang er nog geen
                              // schoonmaker toegewezen is (Ingepland), oranje
                              // zodra die er wel is maar de schoonmaak nog
                              // niet voltooid is (Toegewezen), groen zodra
                              // voltooid of al verstreken (Voltooid).
                              const isPast = new Date(cleaning.end) < now;
                              const isCompleted = isPast || ['COMPLETED', 'INVOICED'].includes(cleaning.extendedProps.status);
                              // Zoals in de echte app (verhuurder): enkel blauw (aankomend) of groen (voltooid).
                              const cleaningStatusClass = isCompleted ? 'month-bar-cleaning-completed' : 'month-bar-cleaning-assigned';
                              const { left, width } = barStyle(cleaning.start, cleaning.end);
                              return (
                                <div
                                  key={`cleaning-${cleaning.id}`}
                                  className={`month-bar month-bar-cleaning ${cleaningStatusClass}`}
                                  style={{ left, width }}
                                  title={`Schoonmaak\n${fmtDateTime(cleaning.start)} → ${fmtDateTime(cleaning.end)}`}
                                  onClick={() => confirmDeleteCleaning(property, cleaning)}
                                />
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                </div>
    );
  };

  return (
    <div className={`dashboard-container ${phoneView ? 'dashboard-container-native demo-phone-view' : ''}`}>
      <nav className="dashboard-nav">
        <button
          className={`dash-nav-toggle ${mobileMenuOpen ? 'open' : ''}`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Menu"
        >
          <span></span><span></span><span></span>
        </button>

        {searchParams.get('embedded') !== '1' && (
          <div className="nav-brand">
            <SmartStaysLogo className="nav-logo" />
          </div>
        )}
        <div className="nav-user">
          {/* Deze demo staat volledig los van de echte app (geen login, geen
              "Terug"-knop in de browser-adresbalk zoals op desktop) - op de
              mobiele app zat je hierdoor vast als je via een gedeelde
              demo-link binnenkwam, want de hardware-terugknop van Android
              werkt niet betrouwbaar/consistent en iOS heeft er sowieso geen.
              Daarom altijd een expliciete, zichtbare sluitknop terug naar de
              publieke site. Niet tonen in de embedded iframe-preview (die
              zit al binnen de gewone marketingpagina op desktop). */}
          {searchParams.get('embedded') !== '1' && (
            <button
              className="demo-exit-btn"
              onClick={() => navigate('/')}
              aria-label="Demo verlaten"
              title="Demo verlaten"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          )}
          <DemoNotificationBell
            properties={properties}
            eventsByProperty={eventsByProperty}
            onNotificationClick={handleNotificationClick}
          />
          <DemoProfileMenu onLogoutClick={handleDemoLogout} />
        </div>

        {mobileMenuOpen && (
          <div className="dash-mobile-menu">
            <div className="dash-mobile-label">Overzicht</div>
            <button
              className={`dash-mobile-item ${mainView === 'multi-calendar' ? 'active' : ''}`}
              onClick={() => { setMainView('multi-calendar'); setMobileMenuOpen(false); }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="4" width="18" height="17" rx="2" stroke="currentColor" strokeWidth="1.7"/>
                <path d="M3 10H21M8 2V6M16 2V6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
              </svg>
              Multi-kalender
            </button>
            {tier !== 'NONE' && (
              <button
                className={`dash-mobile-item ${mainView === 'cleaning' ? 'active' : ''}`}
                onClick={() => { setMainView('cleaning'); setMobileMenuOpen(false); }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11 3L12.6 8.4L18 10L12.6 11.6L11 17L9.4 11.6L4 10L9.4 8.4L11 3Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
                  <path d="M18 14L18.7 16.3L21 17L18.7 17.7L18 20L17.3 17.7L15 17L17.3 16.3L18 14Z" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round"/>
                </svg>
                Schoonmaak
              </button>
            )}
            {automation && (
              <button
                className={`dash-mobile-item ${mainView === 'automation' ? 'active' : ''}`}
                onClick={() => { setMainView('automation'); setMobileMenuOpen(false); }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 19V13M10 19V9M16 19V5M22 19H2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Automation
              </button>
            )}
            <div className="dash-mobile-divider"></div>
            <div className="dash-mobile-label">Mijn accommodaties</div>
            {properties.map((prop) => (
              <div key={prop.id} className={`dash-mobile-item-row ${mainView === 'properties' && selectedPropertyId === prop.id ? 'active' : ''}`}>
                <button
                  className={`dash-mobile-item ${mainView === 'properties' && selectedPropertyId === prop.id ? 'active' : ''}`}
                  onClick={() => { selectProperty(prop); setMobileMenuOpen(false); }}
                >
                  <img src={prop.photoUrl} alt="" className="dash-mobile-item-photo" />
                  {prop.name}
                </button>
                {/* Zelfde bewerk-knop als in de echte Dashboard.js (real app) -
                    ontbrak hier, waardoor de demo dit niet consistent naspiegelde. */}
                <button
                  onClick={(e) => { e.stopPropagation(); setMobileMenuOpen(false); openEditPropertyModal(prop); }}
                  className="edit-property-btn dash-mobile-item-edit"
                  title="Bewerken"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 3L21 7L7 21H3V17L17 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </nav>

      {toast.show && (
        <div className={`toast-message ${toast.type}`}>{toast.message}</div>
      )}

      {/* Eigen bevestigingsmodaltje i.p.v. window.confirm() - askConfirm()
          hierboven vult confirmState.message/onConfirm; de knop voert
          onConfirm uit en sluit het modaltje. */}
      {confirmState && (
        <div className="modal-overlay" onClick={() => setConfirmState(null)}>
          <div className="modal confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Bevestigen</h2>
              <button className="modal-close" onClick={() => setConfirmState(null)}>✖</button>
            </div>
            <div className="modal-body">
              <p style={{ whiteSpace: 'pre-line' }}>{confirmState.message}</p>
            </div>
            <div className="modal-buttons">
              <button type="button" className="cancel-btn" onClick={() => setConfirmState(null)}>
                Annuleren
              </button>
              <button
                type="button"
                className="submit-btn"
                onClick={() => { const { onConfirm } = confirmState; setConfirmState(null); onConfirm(); }}
              >
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="dashboard-layout">
        <aside className="dashboard-sidebar">
          <div className="sidebar-header"><h3>Overzicht</h3></div>
          <button
            className={`sidebar-multicalendar-link ${mainView === 'multi-calendar' ? 'active' : ''}`}
            onClick={() => setMainView('multi-calendar')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="4" width="18" height="17" rx="2" stroke="currentColor" strokeWidth="1.7"/>
              <path d="M3 10H21M8 2V6M16 2V6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
            </svg>
            Multi-kalender
          </button>

          {tier !== 'NONE' && (
            <button
              className={`sidebar-multicalendar-link ${mainView === 'cleaning' ? 'active' : ''}`}
              onClick={() => setMainView('cleaning')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11 3L12.6 8.4L18 10L12.6 11.6L11 17L9.4 11.6L4 10L9.4 8.4L11 3Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
                <path d="M18 14L18.7 16.3L21 17L18.7 17.7L18 20L17.3 17.7L15 17L17.3 16.3L18 14Z" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round"/>
              </svg>
              Schoonmaak
            </button>
          )}

          {automation && (
            <button
              className={`sidebar-multicalendar-link ${mainView === 'automation' ? 'active' : ''}`}
              onClick={() => setMainView('automation')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 19V13M10 19V9M16 19V5M22 19H2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Automation
            </button>
          )}

          <div className="sidebar-header"><h3>Mijn accommodaties</h3></div>
          <ul className="property-list">
            {properties.map((prop) => (
              <li
                key={prop.id}
                className={`property-item ${mainView === 'properties' && selectedPropertyId === prop.id ? 'active' : ''}`}
                onClick={() => selectProperty(prop)}
              >
                <img src={prop.photoUrl} alt="" className="property-photo-thumb" />
                <span className="property-name">{prop.name}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); openEditPropertyModal(prop); }}
                  className="edit-property-btn"
                  title="Bewerken"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 3L21 7L7 21H3V17L17 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  </svg>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <main className="dashboard-main">
          <ErrorBoundary resetKey={`${mainView}|${activeTab}|${selectedPropertyId}`} compact>
          {phoneView && mainView === 'notifications' && (
            <DemoNotificationBell
              screen
              properties={properties}
              eventsByProperty={eventsByProperty}
              onNotificationClick={handleNotificationClick}
              onScreenOpen={() => setDemoUnread(0)}
            />
          )}
          {phoneView && mainView === 'profile' && (
            <DemoProfileMenu screen onOpenProperties={() => setMainView('my-properties')} onLogoutClick={handleDemoLogout} />
          )}
          {phoneView && mainView === 'my-properties' && (
            <div className="native-property-list-wrap">
              <div className="native-detail-head">
                <button type="button" className="native-detail-back" onClick={() => setMainView('profile')} aria-label="Terug">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6" /></svg>
                </button>
                <h2 className="native-detail-title">{dt('Accommodaties')}</h2>
              </div>
              <PropertyPicker properties={properties} onPick={(prop) => openEditPropertyModal(prop)} />
            </div>
          )}
          {phoneView && mainView === 'properties' && !nativePropertyDetail && lockProperties.length > 1 && (
            <div className="native-property-list-wrap">
              <PropertyPicker
                title={dt('Kies een woning')}
                properties={lockProperties}
                onPick={(prop) => { setSelectedPropertyId(prop.id); setActiveTab('smartlock'); setShowSmartlockLog(false); setNativePropertyDetail(true); }}
              />
            </div>
          )}
          {phoneView && mainView === 'multi-calendar' && (
            <DemoCalendarHub
              properties={properties}
              eventsByProperty={eventsByProperty}
              onBookingClick={openBookingModal}
              onCleaningClick={confirmDeleteCleaning}
              renderMonth={renderMonthCalendar}
            />
          )}
          {!phoneView && mainView === 'multi-calendar' && (
            <div className="calendar-section">
              <h2>{dt('Multi-kalender')}</h2>
              <DemoTimeline
                properties={properties}
                eventsByProperty={eventsByProperty}
                onBookingClick={openBookingModal}
                onCleaningClick={confirmDeleteCleaning}
              />
            </div>
          )}

          {!phoneView && mainView === 'cleaning' && (
            <DemoCleaningOverview properties={properties} eventsByProperty={eventsByProperty} />
          )}
          {phoneView && mainView === 'cleaning' && (
            <DemoFilteredCleaning properties={properties} eventsByProperty={eventsByProperty} />
          )}

          {!phoneView && mainView === 'automation' && automation && (
            <AutomationPanel data={buildAutomationData(properties, eventsByProperty)} title="SmartStays Automation" />
          )}
          {phoneView && mainView === 'automation' && automation && (
            <DemoFilteredAutomation properties={properties} eventsByProperty={eventsByProperty} />
          )}

          {mainView === 'properties' && selectedProperty && (!phoneView || nativePropertyDetail) && (
            <>
              {phoneView && (
                <div className="native-detail-head">
                  {lockProperties.length > 1 && (
                    <button type="button" className="native-detail-back" onClick={() => setNativePropertyDetail(false)} aria-label="Alle woningen">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6" /></svg>
                    </button>
                  )}
                  <h2 className="native-detail-title">{selectedProperty.name}</h2>
                </div>
              )}
              <div className="property-tab-buttons" style={phoneView ? { display: 'none' } : undefined}>
                {showAutomationTab && (
                  <button className={`property-tab-btn ${activeTab === 'automation' ? 'active' : ''}`} onClick={() => setActiveTab('automation')}>
                    Automation
                  </button>
                )}
                <button className={`property-tab-btn ${activeTab === 'calendar' ? 'active' : ''}`} onClick={() => setActiveTab('calendar')}>
                  Kalender
                </button>
                {showSmartlockTab && (
                  <button className={`property-tab-btn ${activeTab === 'smartlock' ? 'active' : ''}`} onClick={() => setActiveTab('smartlock')}>
                    Smart lock
                  </button>
                )}
              </div>

              {activeTab === 'calendar' && renderMonthCalendar(selectedProperty)}

              {activeTab === 'smartlock' && showSmartlockTab && (
                <div className="codes-section">
                  <div className="code-card lock-actions-card">
                    <div className="code-header">
                      <span className="code-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                          <path d="M8 11V7C8 4.8 9.8 3 12 3C14.2 3 16 4.8 16 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                      </span>
                      <span className="code-label">{dt('Deurslot')}</span>
                      <div className={`door-current-status ${locked ? 'is-locked' : 'is-unlocked'}`}>
                        {locked ? dt('Vergrendeld') : dt('Ontgrendeld')}
                      </div>
                    </div>
                    <div className="lock-actions-buttons">
                      <button type="button" className="lock-action-btn unlock" onClick={() => handleLockAction(false)}>{dt('Ontgrendel')}</button>
                      <button type="button" className="lock-action-btn lock" onClick={() => handleLockAction(true)}>{dt('Vergrendel')}</button>
                    </div>
                  </div>

                  <div className="code-card battery-card">
                    <div className="code-header">
                      <span className="code-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                          <path d="M21 10V14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                      </span>
                      <span className="code-label">{dt('Batterijstatus')}</span>
                      <button type="button" className="smartlock-sync-btn" onClick={handleSyncSmartlock}>{dt('Synchroniseer')}</button>
                    </div>
                    <div className="battery-level">
                      <div className="battery-bar">
                        <div
                          className={`battery-fill ${battery <= 20 ? 'low' : battery <= 50 ? 'medium' : 'high'}`}
                          style={{ width: `${battery}%` }}
                        />
                      </div>
                      <span className="battery-percentage">{battery}%</span>
                    </div>
                    {/* Keypad-batterij erbij, zoals in de echte app (zie
                        .keypad-battery-status in Dashboard.js) - deze
                        ontbrak nog in de demo. */}
                    <div className="keypad-battery-status">
                      <p className="battery-note">{dt('Batterij keypad OK')}</p>
                    </div>
                  </div>

                  <div className="code-card">
                    <div className="code-header">
                      <span className="code-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                          <path d="M12 7V12L15.5 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <span className="code-label">{dt('Acties')}</span>
                      <button onClick={() => setShowSmartlockLog(!showSmartlockLog)} className="log-toggle-btn">
                        {showSmartlockLog ? dt('Verbergen') : dt('Bekijken')}
                      </button>
                    </div>
                    {showSmartlockLog && (
                      <>
                        {/* Enkel de acties met een herkenbare naam (gastcode =
                            ingecheckt, schoonmaakcode = toegekomen, of een
                            andere persoonlijke code) - zelfde curatie als de
                            echte app, i.p.v. meteen elke losse actie te
                            tonen. */}
                        {importantLog.length === 0 ? (
                          <p className="battery-note">{dt('Geen belangrijke acties gevonden.')}</p>
                        ) : (
                          <ul className="smartlock-log-list">
                            {importantLog.map((entry, i) => (
                              <li key={i} className="smartlock-log-item">
                                <span className="log-action">{entry.action === 1 ? dt('Ontgrendeld') : dt('Vergrendeld')}</span>
                                <span className="log-name"> - {entry.name}</span>
                                <span className="log-date">{entry.date.toLocaleString(demoLocale())}</span>
                              </li>
                            ))}
                          </ul>
                        )}

                        <button
                          type="button"
                          className="log-toggle-btn full-history-toggle"
                          onClick={() => setShowFullSmartlockLog((v) => !v)}
                        >
                          {showFullSmartlockLog ? dt('Verbergen') : dt('Volledige geschiedenis bekijken')}
                        </button>

                        {showFullSmartlockLog && (
                          <ul className="smartlock-log-list full-history">
                            {log.map((entry, i) => (
                              <li key={i} className="smartlock-log-item">
                                <span className="log-action">{entry.action === 1 ? dt('Ontgrendeld') : dt('Vergrendeld')}</span>
                                {entry.name && <span className="log-name"> - {entry.name}</span>}
                                <span className="log-date">{entry.date.toLocaleString(demoLocale())}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </>
                    )}
                  </div>

                  {/* Gastcode + persoonlijke codes: stonden voorheen in een
                      eigen "Codes"-tab, nu samengevoegd in Smart lock, meteen
                      onder Acties - zelfde als de echte app. */}
                  <div className="code-card guest-code-card">
                    <div className="code-header">
                      <span className="code-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2C8.13 2 5 5.13 5 9V12C3.5 12.5 3 13 3 15V18C3 19.1 3.9 20 5 20H19C20.1 20 21 19.1 21 18V15C21 13 20.5 12.5 19 12V9C19 5.13 15.87 2 12 2Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                          <path d="M9 22L12 20L15 22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                      </span>
                      <span className="code-label">{dt('Huidige gastcode')}</span>
                    </div>
                    {!currentGuestCode.active && (
                      <p className="battery-note">{dt('Geen gast momenteel ingecheckt')}</p>
                    )}
                    {currentGuestCode.active && (
                      <>
                        <div className="code-value">{currentGuestCode.code}</div>
                        <div className="code-expiry">{dt('{{name}} - vervalt bij check-out', { name: currentGuestCode.guestName })}</div>
                      </>
                    )}
                  </div>

                  <div className="personal-codes-header">
                    <h3>{dt('Persoonlijke codes')}</h3>
                    {codes.length < 4 && (
                      <button onClick={openAddCodeModal} className="add-code-btn">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        {dt('Code toevoegen')}
                      </button>
                    )}
                  </div>

                  <div className="codes-grid">
                    {codes.map((code) => (
                      <div key={code.id} className="code-card personal-code-card">
                        <div className="code-header">
                          <span className="code-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M20 21V19C20 16.8 18.2 15 16 15H8C5.8 15 4 16.8 4 19V21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                              <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                            </svg>
                          </span>
                          <span className="code-label">{code.name}</span>
                        </div>
                        <div className="code-value">{code.code}</div>
                        <div className="code-actions">
                          <button onClick={() => openEditCodeModal(code)} className="icon-btn" title="Bewerken">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M17 3L21 7L7 21H3V17L17 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                            </svg>
                          </button>
                          <button onClick={() => handleDeleteCode(code.id, code.name)} className="icon-btn delete-icon" title="Verwijderen">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M4 7H20M10 11V16M14 11V16M5 7L6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19L19 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                              <path d="M9 7V4C9 3.4 9.4 3 10 3H14C14.6 3 15 3.4 15 4V7" stroke="currentColor" strokeWidth="1.5"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'automation' && showAutomationTab && (
                <AutomationPanel data={buildAutomationData([selectedProperty], eventsByProperty)} title={`Automation - ${selectedProperty.name}`} />
              )}
            </>
          )}
          </ErrorBoundary>
        </main>
      </div>

      {/* Modal: woning bewerken */}
      {showEditPropertyModal && editingProperty && (
        <div className="modal-overlay" onClick={() => setShowEditPropertyModal(false)}>
          <div className="modal property-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Woning bewerken</h2>
              <button className="modal-close" onClick={() => setShowEditPropertyModal(false)}>✖</button>
            </div>
            <form onSubmit={handleEditPropertySave} noValidate onInput={clearFieldInvalid}>
              <div className="form-group">
                <label>Naam</label>
                <input type="text" value={editingProperty.name} onChange={(e) => setEditingProperty({ ...editingProperty, name: e.target.value })} required />
              </div>
              {!phoneView && (
              <div className="form-group">
                <label>Adres</label>
                <input type="text" value={editingProperty.address || ''} onChange={(e) => setEditingProperty({ ...editingProperty, address: e.target.value })} />
              </div>
              )}
              <div className="form-group">
                <label>Foto</label>
                {editingProperty.photoUrl ? (
                  <div className="property-photo-current">
                    <img src={editingProperty.photoUrl} alt="" className="property-photo-preview" />
                    <button type="button" className="action-btn" onClick={() => showToast('Demo: foto verwijderen wordt hier niet echt uitgevoerd', 'success')}>
                      Verwijderen
                    </button>
                  </div>
                ) : (
                  <p className="empty-state-text">Nog geen foto</p>
                )}
                <label className="submit-btn" style={{ display: 'inline-block', width: 'auto', cursor: 'pointer' }}>
                  {editingProperty.photoUrl ? 'Foto vervangen' : 'Foto uploaden'}
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={() => showToast('Demo: bestand wordt hier niet echt opgeslagen', 'success')} />
                </label>
              </div>
              {editingProperty.cleaningTier !== 'NONE' && (
              <div className="form-group">
                <label>Schoonmaakinstructies (PDF)</label>
                <p className="empty-state-text">Nog geen instructies</p>
                <label className="submit-btn" style={{ display: 'inline-block', width: 'auto', cursor: 'pointer' }}>
                  PDF uploaden
                  <input type="file" accept="application/pdf" style={{ display: 'none' }} onChange={() => showToast('Demo: bestand wordt hier niet echt opgeslagen', 'success')} />
                </label>
              </div>
              )}
              <div className="modal-buttons">
                <button type="button" onClick={() => setShowEditPropertyModal(false)} className="cancel-btn">Annuleren</button>
                <button type="submit" className="submit-btn">Opslaan</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: schoonmaak inplannen */}
      {showCleaningModal && (
        <div className="modal-overlay" onClick={resetCleaningForm}>
          <div className="modal cleaning-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Schoonmaak inplannen</h2>
              <button className="modal-close" onClick={resetCleaningForm}>✖</button>
            </div>
            <form onSubmit={handleSaveCleaning} noValidate onInput={clearFieldInvalid}>
              {properties.length > 1 && (
                <div className="form-group">
                  <label>Woning *</label>
                  <select value={cleaningModalPropertyId} onChange={(e) => handleSelectCleaningModalProperty(e.target.value)} required>
                    <option value="">Kies een woning</option>
                    {properties.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>Boeking (optioneel)</label>
                <select value={selectedBookingId} onChange={(e) => handleSelectBooking(e.target.value)} disabled={!cleaningModalPropertyId}>
                  <option value="">Geen boeking</option>
                  {unscheduledForModal.map((b) => (
                    <option key={b.id} value={b.id}>{b.guestName} ({fmtDateOnly(b.checkIn)} - {fmtDateOnly(b.checkOut)})</option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start *</label>
                  <DatePicker
                    selected={cleaningForm.startDate}
                    onChange={(date) => setCleaningForm((prev) => {
                      const duration = prev.endDate.getTime() - prev.startDate.getTime();
                      return { ...prev, startDate: date, endDate: new Date(date.getTime() + duration) };
                    })}
                    showTimeSelect
                    dateFormat="dd/MM/yyyy HH:mm"
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    className="datetime-picker"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Einde *</label>
                  <DatePicker
                    selected={cleaningForm.endDate}
                    onChange={(date) => setCleaningForm({ ...cleaningForm, endDate: date })}
                    showTimeSelect
                    dateFormat="dd/MM/yyyy HH:mm"
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    className="datetime-picker"
                    required
                  />
                </div>
              </div>

              <div className="modal-buttons">
                <button type="button" onClick={resetCleaningForm} className="cancel-btn">Annuleren</button>
                <button type="submit" className="submit-btn">Inplannen</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: boekingdetails */}
      {bookingModalData && (
        <div className="modal-overlay" onClick={() => setBookingModalData(null)}>
          <div className="modal booking-info-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><PlatformBadge platform={bookingModalData.platform} />{bookingModalData.guestName}{bookingModalData.platform ? ` - ${bookingModalData.platform}` : ''}</h2>
              <button className="modal-close" onClick={() => setBookingModalData(null)}>✖</button>
            </div>
            <div className="booking-info-body">
              <p><strong>Woning:</strong> {bookingModalData.propertyName}</p>
              <p><strong>Start:</strong> {fmtDateTime(bookingModalData.checkIn)}</p>
              <p><strong>Einde:</strong> {fmtDateTime(bookingModalData.checkOut)}</p>
              <p><strong>Gastnaam:</strong> {bookingModalData.guestName}</p>
              <p><strong>E-mail gast:</strong> {bookingModalData.guestEmail}</p>
            </div>
            {/* Klant zonder schoonmaak: niets over schoonmaak tonen. */}
            {bookingModalData.cleaningTier !== 'NONE' && (
            <div className="booking-info-cleaning-section">
              {bookingModalData.hasNoCleaning ? (
                <button
                  type="button"
                  className="submit-btn"
                  onClick={() => {
                    const { id, propertyId } = bookingModalData;
                    setBookingModalData(null);
                    handleOpenCleaningModal(propertyId, id);
                  }}
                >
                  Nu schoonmaak inplannen
                </button>
              ) : (
                <p className="guest-info-note">Schoonmaak is al ingepland</p>
              )}
            </div>
            )}
          </div>
        </div>
      )}

      {/* Modal: code toevoegen */}
      {showAddCodeModal && (
        <div className="modal-overlay" onClick={() => setShowAddCodeModal(false)}>
          <div className="modal code-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Code toevoegen</h2>
              <button className="modal-close" onClick={() => setShowAddCodeModal(false)}>✖</button>
            </div>
            <div className="modal-body">
              <div className="warning-box">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
                  <path d="M12 8V12M12 16H12.01M3 12C3 16.97 7.03 21 12 21C16.97 21 21 16.97 21 12C21 7.03 16.97 3 12 3C7.03 3 3 7.03 3 12Z" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
                <span>Deze code werkt permanent op het slot, in tegenstelling tot de tijdelijke gastcodes.</span>
              </div>
              <div className="form-group">
                <label>Naam (optioneel)</label>
                <input type="text" value={codeForm.name} onChange={(e) => setCodeForm({ ...codeForm, name: e.target.value })} placeholder="Bijv. Mijn code, Vrouw, Kind" />
              </div>
              <div className="form-group">
                <label>Code</label>
                <input
                  type="text"
                  value={codeForm.code}
                  onChange={(e) => setCodeForm({ ...codeForm, code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                  placeholder="123456"
                  maxLength="6"
                  className="code-input"
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <label>Herhaal code</label>
                <input
                  type="text"
                  value={codeForm.confirm}
                  onChange={(e) => setCodeForm({ ...codeForm, confirm: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                  placeholder="123456"
                  maxLength="6"
                  className="code-input"
                  autoComplete="off"
                />
              </div>
              {codeError && <div className="error-message">{codeError}</div>}
            </div>
            <div className="modal-buttons">
              <button onClick={() => setShowAddCodeModal(false)} className="cancel-btn">Annuleren</button>
              <button onClick={handleAddCode} className="submit-btn">Toevoegen</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: code wijzigen */}
      {showEditCodeModal && (
        <div className="modal-overlay" onClick={() => setShowEditCodeModal(false)}>
          <div className="modal code-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Code wijzigen</h2>
              <button className="modal-close" onClick={() => setShowEditCodeModal(false)}>✖</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Naam</label>
                <input type="text" value={codeForm.name} onChange={(e) => setCodeForm({ ...codeForm, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Nieuwe code</label>
                <input
                  type="text"
                  value={codeForm.code}
                  onChange={(e) => setCodeForm({ ...codeForm, code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                  placeholder="123456"
                  maxLength="6"
                  className="code-input"
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <label>Herhaal code</label>
                <input
                  type="text"
                  value={codeForm.confirm}
                  onChange={(e) => setCodeForm({ ...codeForm, confirm: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                  placeholder="123456"
                  maxLength="6"
                  className="code-input"
                  autoComplete="off"
                />
              </div>
              {codeError && <div className="error-message">{codeError}</div>}
            </div>
            <div className="modal-buttons">
              <button onClick={() => setShowEditCodeModal(false)} className="cancel-btn">Annuleren</button>
              <button onClick={handleEditCode} className="submit-btn">Opslaan</button>
            </div>
          </div>
        </div>
      )}

      {phoneView && createPortal(
        <nav className="native-tabbar">
          <button type="button" className={`native-tab ${mainView === 'multi-calendar' ? 'active' : ''}`} aria-label={dt('Multi-kalender')} onClick={() => setMainView('multi-calendar')}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="17" rx="2" stroke="currentColor" strokeWidth="1.8"/><path d="M3 10H21M8 2V6M16 2V6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
          </button>
          {tier !== 'NONE' && (
            <button type="button" className={`native-tab ${mainView === 'cleaning' ? 'active' : ''}`} aria-label={dt('Schoonmaak')} onClick={() => setMainView('cleaning')}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M11 3L12.6 8.4L18 10L12.6 11.6L11 17L9.4 11.6L4 10L9.4 8.4L11 3Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/><path d="M18 14L18.7 16.3L21 17L18.7 17.7L18 20L17.3 17.7L15 17L17.3 16.3L18 14Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/></svg>
            </button>
          )}
          {hasLock && (
            <button type="button" className={`native-tab ${mainView === 'properties' ? 'active' : ''}`} aria-label="Smart lock" onClick={() => { setMainView('properties'); setNativePropertyDetail(false); }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4.5" y="10.5" width="15" height="10" rx="2.2" /><path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" /><circle cx="12" cy="15.5" r="1.3" /></svg>
            </button>
          )}
          {automation && (
            <button type="button" className={`native-tab ${mainView === 'automation' ? 'active' : ''}`} aria-label="Automation" onClick={() => setMainView('automation')}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M4 19V13M10 19V9M16 19V5M22 19H2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          )}
          <button type="button" className={`native-tab ${mainView === 'notifications' ? 'active' : ''}`} aria-label={dt('Meldingen')} onClick={() => setMainView('notifications')}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>
            {demoUnread > 0 && <span className="native-tab-badge">{demoUnread}</span>}
          </button>
          <button type="button" className={`native-tab ${mainView === 'profile' || mainView === 'my-properties' ? 'active' : ''}`} aria-label="Profiel" onClick={() => setMainView('profile')}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" /></svg>
          </button>
        </nav>,
        document.body
      )}
    </div>
  );
}

export default DemoDashboard;
